// @ts-check
// Editor estructurado de expresión simple de un nivel (TSK-0049).
//
// Contexto (ver historial de TSK-0049 en tasks/review/ y comentario de
// TSK-0022 en palette-view.js): el editor visual coloca bloques con
// contenido de RELLENO fijo (p.ej. "Entrada" siempre crea
// `variable: 'variable'`) y no existía ningún widget para editar el
// contenido real de una expresión. Este módulo es esa pieza: dada una
// expresión actual (o `null` para construir una desde cero), permite
// reconstruirla con controles nativos accesibles y notifica el resultado
// por callback. NO se integra todavía en ningún panel del editor (eso es
// TSK-0050/0051): es un widget reutilizable, aislado.
//
// Alcance: una expresión editable aquí es (a) un literal, (b) una referencia
// a variable, o (c) una operación binaria cuyos operandos pueden ser, cada
// uno, cualquiera de las tres cosas -- incluida OTRA OPERACIÓN. Sin parser de
// texto libre.
//
// ## El anidamiento es LIBRE desde 2026-08-07 (DEC-115)
//
// Hasta entonces DEC-024 lo limitaba a un nivel, y esta cabecera afirmaba que
// "un operando JAMÁS puede llegar a mostrar ni crear una operación anidada".
// Se levantó porque el traductor de Python SÍ produce anidadas -- es lo que
// significa `print(a + " juega " + b)` -- y abrir ese bloque lanzaba un
// `TypeError` que se llevaba por delante el panel entero. Ver DEC-115.
//
// Cómo se sostiene sin recursión infinita: `construirSelectorExpresion` es la
// ÚNICA que sabe construir literal/referencia/operación, y ahora invoca a sus
// dos operandos con los TRES modos. Si montara la sección "Operación" de
// forma ansiosa, cada operando montaría la suya y los suyos la suya, sin fin.
// Por eso `montarSeccionOperacion` es PEREZOSA: se monta cuando la expresión
// que llega ya es una operación, o cuando el alumno elige ese modo. La
// recursión la corta el propio dato, que es finito.
//
// ## Lo que las vistas GUIADAS no saben dibujar
//
// La vista guiada de valor sabe con valores simples y con CADENAS sin
// paréntesis (`nombre + " juega en " + equipo`, `c * 9 / 5 + 32`): se
// construyen y se leen con la precedencia habitual (`analizarValor`,
// `construirArbolConPrecedencia`). Ante algo que una cadena no expresa
// (`peso / (altura * altura)`, llamadas…) cede al editor técnico, igual que la
// vista de condición hace con `analizarCondicion`; y las dos ofrecen además
// «Opciones avanzadas» (`crearOpcionesAvanzadas`). Sin ese fallback se pintaba
// el operando anidado como un campo de variable VACÍO y el trabajo del alumno
// desaparecía al tocarlo: lo encontró architecture-reviewer el 2026-08-07.
//
// Reutilización de arquitectura (TSK-0021, editor-model.js NO se edita en
// esta tarea): los 3 tipos de nodo se construyen EXCLUSIVAMENTE con
// `crearLiteral`/`crearReferenciaVariable`/`crearOperacion`. Este módulo
// nunca fabrica a mano la forma de un nodo del modelo lógico.
//
// Inmutabilidad (docs/03-frontend.md, mismo criterio que
// insertarEnSecuencia/eliminarDeSecuencia de editor-model.js): la expresión
// recibida en `expresionInicial` solo se LEE (para precargar los
// controles); nunca se reasignan sus campos ni los de sus operandos. Cada
// cambio del usuario construye un nodo NUEVO con las factorías reales y lo
// entrega por `onCambio(nuevaExpresion)`.
//
// Ambigüedad no resuelta por las fuentes, decisión tomada aquí (repórtese
// en el historial de la tarea): de dónde sale la lista de variables ya
// declaradas para el operando "referencia a variable". Ni docs/03-frontend.md
// §13 ni editor-model.js definen ese origen (lo resolverá el panel que
// integre este widget, TSK-0050/0051, que sí conoce el `Programa`
// completo). Se decide que el widget RECIBE esa lista ya resuelta como
// `variablesDisponibles` (array de nombres, opcional, por defecto `[]`) y
// la usa SOLO como sugerencia de autocompletado nativo (`<datalist>`) sobre
// un `<input type="text">` de texto libre -- nunca como `<select>` cerrado
// -- por dos razones: (1) sigue funcionando razonablemente cuando la lista
// está vacía o incompleta (p.ej. si el panel todavía no sabe listar
// variables), y (2) el propio backend (`backend/src/app/domain/ir.py` /
// análisis semántico) es quien de verdad valida la existencia de la
// variable; este widget solo valida FORMATO de identificador como red de
// seguridad mínima de UI, no la existencia real.
//
// ---------------------------------------------------------------------
// TSK-0069: refactor de UX (sin tocar el modelo de datos ni el contrato
// público que consume block-content-panel.js).
// ---------------------------------------------------------------------
// Motivación (historial de la tarea): el usuario, probando la app en vivo
// (2026-07-17), reportó que "la forma de poner la condición es muy fea y
// poco intuitiva". El caso real y más común (DEC-024, ejercicios sembrados:
// 'edad < 12', 'edad <= 17', 'temperatura >= 40.0') es SIEMPRE una
// comparación "variable OP literal": el operando izquierdo es una
// referencia y el derecho un literal. La versión anterior defaulteaba
// TODOS los niveles (incluidos ambos operandos) a modo "Literal" -- el
// alumno tenía que además abrir el operando izquierdo y cambiarlo a
// "Variable" a mano. Cambios de esta tarea:
//   1) El operando IZQUIERDO de una operación ahora ofrece sus modos en el
//      orden `[REFERENCIA, LITERAL]` (el operando DERECHO mantiene
//      `[LITERAL, REFERENCIA]`): como `determinarModoInicial` usa el primer
//      modo permitido como valor por defecto de un nodo nuevo, una
//      operación recién creada arranca YA en la forma más habitual
//      ("variable OP valor"), ahorrando un clic de radio en el caso común
//      sin eliminar la posibilidad de invertirlo.
//   2) El grupo de radios interno de cada operando (antes rotulado también
//      "Tipo de expresión", igual que el nivel superior, generando
//      confusión sobre a qué nivel se refería cada grupo) se rotula ahora
//      "Tipo de operando" (parámetro `etiquetaLegendModo`), y su estilo
//      (expression-editor.css) es más compacto/en línea, para que un
//      operando se lea como parte de la fila operando-operador-operando en
//      vez de como una sub-tarjeta separada con su propio formulario.
//   3) CSS: los tres controles de una operación (operando izquierdo,
//      operador, operando derecho) se distinguen visualmente como un grupo
//      de una sola fila (clase `expr-editor-grupo--operando`, borde más
//      ligero, disposición en línea) en vez de dos cajas apiladas
//      idénticas a la del nivel superior.
// Contradicción registrada (CLAUDE.md "no decidas silenciosamente"): el
// criterio de aceptación de TSK-0069 dice textualmente "Sigue siendo
// posible construir una operación con un operando anidado (otra
// operación)". Tomado literalmente esto contradice DEC-024, que limita
// expression-editor.js a UN nivel y difiere explícitamente "anidamiento
// arbitrario de expresiones" como capacidad futura -- y la propia
// arquitectura de este módulo (`determinarModoInicial`/`modosPermitidos`)
// impide estructuralmente que un operando ofrezca el modo "Operación" (ver
// más arriba, "Cómo se aplica DEC-024 estructuralmente"). El mismo
// criterio aclara a continuación su intención real: "no se pierde
// capacidad expresiva RESPECTO A DEC-024", es decir, respecto a la que ya
// existía (una operación de un nivel, sin anidar). Se resuelve interpretando
// el criterio como "no reducir" la capacidad ya existente, NO como "añadir"
// anidamiento arbitrario: esta tarea sigue sin permitir que un operando sea
// a su vez una `Operacion` (se mantiene la restricción de DEC-024 y de sus
// tests, ver `expression-editor.spec.js`, sección "DEC-024: alcance de un
// nivel"). Ampliar esa capacidad requeriría una decisión de producto propia
// (fuera del alcance "refactor de interfaz" de esta tarea) y está fuera de
// `rutas_permitidas`/`rutas_prohibidas` tocar editor-model.js para ello.
//
// SUPERADO por DEC-115 (2026-08-07): el anidamiento es libre en el editor
// técnico (ver "El anidamiento es LIBRE" arriba) y la vista guiada de valor
// construye cadenas de más de dos valores (ejercicio "presentacion-jugador").
// El párrafo anterior se conserva como historial de TSK-0069; ya no describe
// el comportamiento del módulo.

import {
  TIPO_NODO,
  TIPO_PRIMITIVO,
  OPERADOR,
  crearLiteral,
  crearReferenciaVariable,
  crearOperacion,
} from './editor-model.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 */

/** Los 3 modos de expresión de nivel superior; los operandos de una
 * operación solo permiten los 2 primeros (ver cabecera del módulo). */
const MODO = Object.freeze({
  LITERAL: 'literal',
  REFERENCIA: 'referencia',
  OPERACION: 'operacion',
});

/**
 * Modo de PRESENTACIÓN del widget público `crearEditorExpresion` (ampliación
 * aditiva y retrocompatible, "corte 1"). `'tecnico'` es el editor estructurado
 * de siempre (radios de modo + tipo + operandos) y es el valor por defecto:
 * omitir el parámetro, o pasar `'tecnico'`, produce EXACTAMENTE el mismo DOM y
 * comportamiento que antes de esta ampliación. `'guiado'` queda reservado para
 * la vista didáctica del bloque Condición; su construcción se implementa en el
 * corte siguiente. Hasta entonces se acepta como valor válido pero se resuelve
 * con el camino técnico COMPLETO (nunca un editor a medias), sin ningún
 * consumidor que lo active todavía (ver `crearEditorExpresion`).
 * @type {Readonly<{ TECNICO: 'tecnico', GUIADO: 'guiado' }>}
 */
export const MODO_PRESENTACION = Object.freeze({
  TECNICO: 'tecnico',
  GUIADO: 'guiado',
});

/** Etiquetas visibles de cada tipo primitivo (TIPO_PRIMITIVO de editor-model.js). */
const ETIQUETA_TIPO_PRIMITIVO = Object.freeze({
  [TIPO_PRIMITIVO.ENTERO]: 'Entero',
  [TIPO_PRIMITIVO.DECIMAL]: 'Decimal',
  [TIPO_PRIMITIVO.CADENA]: 'Texto',
  [TIPO_PRIMITIVO.BOOLEANO]: 'Booleano',
});

/**
 * Operadores BINARIOS únicamente (DEC-024: "operación binaria de un solo
 * nivel"). `OPERADOR.NO`/`OPERADOR.NEGACION` son unarios en editor-model.js
 * (ver `OPERADORES_UNARIOS`, no exportado); se excluyen aquí a propósito en
 * vez de intentar reconstruir esa lista interna: este widget solo necesita
 * saber cuáles NO ofrecer.
 */
const OPERADORES_BINARIOS = Object.freeze(
  Object.values(OPERADOR).filter((op) => op !== OPERADOR.NO && op !== OPERADOR.NEGACION)
);

/** Etiquetas visibles de cada operador binario. */
const ETIQUETA_OPERADOR = Object.freeze({
  [OPERADOR.SUMA]: 'Suma (+)',
  [OPERADOR.RESTA]: 'Resta (−)',
  [OPERADOR.MULTIPLICACION]: 'Multiplicación (×)',
  [OPERADOR.DIVISION]: 'División (÷)',
  [OPERADOR.MODULO]: 'Módulo (%)',
  [OPERADOR.IGUAL]: 'Igual a (==)',
  [OPERADOR.DISTINTO]: 'Distinto de (!=)',
  [OPERADOR.MENOR]: 'Menor que (<)',
  [OPERADOR.MENOR_IGUAL]: 'Menor o igual que (<=)',
  [OPERADOR.MAYOR]: 'Mayor que (>)',
  [OPERADOR.MAYOR_IGUAL]: 'Mayor o igual que (>=)',
  [OPERADOR.Y]: 'Y (lógico)',
  [OPERADOR.O]: 'O (lógico)',
});

/** Formato mínimo de identificador de variable: letra/guion bajo inicial,
 * luego letras/dígitos/guion bajo. Solo valida FORMATO, no existencia (ver
 * cabecera del módulo). */
const PATRON_IDENTIFICADOR = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

let contadorInstancias = 0;

/**
 * Crea un `<option>` con `textContent`/`value` (nunca `new Option(...)`:
 * ese constructor DOM no está declarado como global en eslint.config.js,
 * igual que `HTMLButtonElement` en palette-view.js; se evita como
 * identificador de código en tiempo de ejecución, ver nota junto a
 * `resolverBotonDeBloque` en ese fichero).
 * @param {string} texto
 * @param {string} valor
 * @returns {HTMLElement}
 */
function crearOpcion(texto, valor) {
  const opcion = document.createElement('option');
  opcion.value = valor;
  opcion.textContent = texto;
  return opcion;
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 */
function requerirFuncion(valor, nombreCampo) {
  if (typeof valor !== 'function') {
    throw new TypeError(`${nombreCampo} debe ser una función`);
  }
  return valor;
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {string[]}
 */
function requerirArrayDeCadenas(valor, nombreCampo) {
  if (!Array.isArray(valor) || !valor.every((elemento) => typeof elemento === 'string')) {
    throw new TypeError(`${nombreCampo} debe ser un array de cadenas`);
  }
  return valor;
}

/**
 * @param {unknown} expresion
 * @returns {expresion is NodoModelo}
 */
function esNodoDeExpresion(expresion) {
  return (
    typeof expresion === 'object' &&
    expresion !== null &&
    typeof (/** @type {any} */ (expresion).nodo) === 'string'
  );
}

/**
 * Determina el modo inicial a partir de una expresión existente, validando
 * a la vez que su tipo de nodo esté permitido en este contexto.
 * @param {NodoModelo | null} expresion
 * @param {string[]} modosPermitidos
 * @returns {string}
 */
function determinarModoInicial(expresion, modosPermitidos) {
  if (expresion === null || expresion === undefined) return modosPermitidos[0];
  if (!esNodoDeExpresion(expresion)) {
    throw new TypeError('expresionInicial debe ser un nodo del modelo (Literal/ReferenciaVariable/Operacion) o null');
  }
  if (expresion.nodo === TIPO_NODO.LITERAL && modosPermitidos.includes(MODO.LITERAL)) return MODO.LITERAL;
  if (expresion.nodo === TIPO_NODO.REFERENCIA_VARIABLE && modosPermitidos.includes(MODO.REFERENCIA)) {
    return MODO.REFERENCIA;
  }
  if (expresion.nodo === TIPO_NODO.OPERACION && modosPermitidos.includes(MODO.OPERACION)) {
    const operandos = /** @type {unknown} */ (expresion.operandos);
    if (!Array.isArray(operandos) || operandos.length !== 2) {
      throw new TypeError(
        'expression-editor solo admite operaciones BINARIAS: se esperaban 2 operandos'
      );
    }
    return MODO.OPERACION;
  }
  throw new TypeError(
    `expresionInicial de tipo '${expresion.nodo}' no está permitida aquí (modos permitidos: ${modosPermitidos.join(', ')})`
  );
}

/**
 * @param {string} tipo
 * @param {string} crudo
 * @returns {{ valido: true, valor: number | string | boolean } | { valido: false, mensaje: string }}
 */
function validarValorLiteral(tipo, crudo) {
  const texto = crudo.trim();
  if (tipo === TIPO_PRIMITIVO.ENTERO) {
    if (!/^-?\d+$/.test(texto)) {
      return { valido: false, mensaje: 'Introduce un número entero, sin decimales (p. ej. 12 o -3).' };
    }
    return { valido: true, valor: Number(texto) };
  }
  if (tipo === TIPO_PRIMITIVO.DECIMAL) {
    if (texto === '' || Number.isNaN(Number(texto))) {
      return { valido: false, mensaje: 'Introduce un número decimal válido (p. ej. 40 o 3.5).' };
    }
    return { valido: true, valor: Number(texto) };
  }
  if (tipo === TIPO_PRIMITIVO.CADENA) {
    return { valido: true, valor: crudo };
  }
  if (tipo === TIPO_PRIMITIVO.BOOLEANO) {
    return { valido: true, valor: texto === 'true' };
  }
  return { valido: false, mensaje: 'Tipo primitivo no reconocido.' };
}

/**
 * @param {string} nombre
 * @returns {{ valido: true, valor: string } | { valido: false, mensaje: string }}
 */
function validarNombreVariable(nombre) {
  const texto = nombre.trim();
  if (!PATRON_IDENTIFICADOR.test(texto)) {
    return {
      valido: false,
      mensaje: 'Escribe un nombre de variable válido: empieza por una letra o "_", sin espacios.',
    };
  }
  return { valido: true, valor: texto };
}

/**
 * @param {HTMLElement} contenedor
 * @param {string} mensaje
 * @param {string} idMensaje
 * @param {HTMLElement} control
 */
function establecerMensajeError(contenedor, mensaje, idMensaje, control) {
  contenedor.textContent = mensaje;
  if (mensaje) {
    control.setAttribute('aria-describedby', idMensaje);
    control.setAttribute('aria-invalid', 'true');
  } else {
    control.removeAttribute('aria-describedby');
    control.removeAttribute('aria-invalid');
  }
}

/**
 * Construye un selector de expresión (recursivo: se reutiliza tanto para el
 * widget de nivel superior como para cada operando de una operación, ver
 * cabecera del módulo). No exportado: `crearEditorExpresion` es la única
 * puerta de entrada pública.
 * @param {{
 *   idBase: string,
 *   etiquetaLegend: string,
 *   expresionInicial: NodoModelo | null,
 *   variablesDisponibles: string[],
 *   modosPermitidos: string[],
 *   onCambio: (expresion: NodoModelo) => void,
 *   etiquetaLegendModo?: string,
 *   esOperando?: boolean,
 * }} opciones
 * @returns {{ elemento: HTMLFieldSetElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void }}
 */
function construirSelectorExpresion({
  idBase,
  etiquetaLegend,
  expresionInicial,
  variablesDisponibles,
  modosPermitidos,
  onCambio,
  // TSK-0069: cuando este selector representa un OPERANDO de una operación
  // (no el nivel superior) recibe un rótulo de grupo más corto y una clase
  // CSS distinta (ver cabecera del módulo) para leerse como parte de una
  // sola fila "operando-operador-operando", no como una sub-tarjeta idéntica
  // a la del nivel superior.
  etiquetaLegendModo = 'Tipo de expresión',
  esOperando = false,
}) {
  let modo = determinarModoInicial(expresionInicial, modosPermitidos);
  const destructores = /** @type {Array<() => void>} */ ([]);

  const raiz = document.createElement('fieldset');
  raiz.className = esOperando ? 'expr-editor-grupo expr-editor-grupo--operando' : 'expr-editor-grupo';

  const legend = document.createElement('legend');
  legend.className = 'expr-editor-legend';
  legend.textContent = etiquetaLegend;
  raiz.append(legend);

  // ---- Selector de modo (radios nativos, docs/03-frontend.md §13: control
  // nativo, no div+ARIA). Fieldset/legend PROPIO (anidado dentro del
  // fieldset raíz) para que el grupo de radios tenga su propio nombre
  // accesible ("Tipo de expresión"), distinto de la etiqueta general del
  // selector (`etiquetaLegend`, p.ej. "Operando izquierdo"). -------------
  const grupoModo = document.createElement('fieldset');
  grupoModo.className = esOperando ? 'expr-editor-modo expr-editor-modo--operando' : 'expr-editor-modo';
  const legendModo = document.createElement('legend');
  legendModo.className = 'expr-editor-modo-legend';
  legendModo.textContent = etiquetaLegendModo;
  grupoModo.append(legendModo);
  /** @type {Record<string, string>} */
  const ETIQUETA_MODO = {
    [MODO.LITERAL]: 'Valor fijo',
    [MODO.REFERENCIA]: 'Variable',
    [MODO.OPERACION]: 'Operación',
  };
  for (const opcionModo of modosPermitidos) {
    const idRadio = `${idBase}-modo-${opcionModo}`;
    const wrapper = document.createElement('span');
    wrapper.className = 'expr-editor-modo-opcion';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = `${idBase}-modo`;
    radio.id = idRadio;
    radio.value = opcionModo;
    radio.checked = opcionModo === modo;

    const etiquetaRadio = document.createElement('label');
    etiquetaRadio.setAttribute('for', idRadio);
    etiquetaRadio.textContent = ETIQUETA_MODO[opcionModo];

    wrapper.append(radio, etiquetaRadio);
    grupoModo.append(wrapper);
  }
  raiz.append(grupoModo);

  // ---- Sección literal ---------------------------------------------------
  const seccionLiteral = document.createElement('div');
  seccionLiteral.className = 'expr-editor-seccion';

  const idTipo = `${idBase}-literal-tipo`;
  const etiquetaTipo = document.createElement('label');
  etiquetaTipo.setAttribute('for', idTipo);
  etiquetaTipo.textContent = 'Tipo de valor';

  const selectTipo = document.createElement('select');
  selectTipo.id = idTipo;
  for (const tipo of Object.values(TIPO_PRIMITIVO)) {
    selectTipo.append(crearOpcion(ETIQUETA_TIPO_PRIMITIVO[tipo], tipo));
  }

  const idValor = `${idBase}-literal-valor`;
  const etiquetaValor = document.createElement('label');
  etiquetaValor.setAttribute('for', idValor);
  etiquetaValor.textContent = 'Valor';

  const contenedorValor = document.createElement('span');
  contenedorValor.className = 'expr-editor-valor-contenedor';

  const idErrorLiteral = `${idBase}-literal-error`;
  const errorLiteral = document.createElement('p');
  errorLiteral.className = 'expr-editor-error';
  errorLiteral.id = idErrorLiteral;
  errorLiteral.setAttribute('role', 'alert');

  seccionLiteral.append(etiquetaTipo, selectTipo, etiquetaValor, contenedorValor, errorLiteral);

  // Valores de precarga (literal existente) o por defecto (nodo nuevo).
  const literalPrevio =
    expresionInicial && expresionInicial.nodo === TIPO_NODO.LITERAL ? expresionInicial : null;
  if (literalPrevio) {
    selectTipo.value = /** @type {string} */ (literalPrevio.tipo);
  }

  /** @type {HTMLInputElement | HTMLSelectElement | null} */
  let controlValor = null;

  /** Reconstruye el control de "Valor" acorde al tipo primitivo elegido:
   * <select> Verdadero/Falso para booleano (nunca reimplementado con
   * div+ARIA), <input type="number"> para entero/decimal, <input
   * type="text"> para cadena. Preserva el valor inicial solo la primera
   * vez (si el tipo coincide con el de `literalPrevio`); en cambios de
   * tipo posteriores usa un valor por defecto coherente en vez de arrastrar
   * un valor potencialmente inválido para el nuevo tipo. */
  function renderizarControlValor() {
    contenedorValor.textContent = '';
    const tipo = selectTipo.value;
    const usarValorPrevio = literalPrevio !== null && literalPrevio.tipo === tipo;

    if (tipo === TIPO_PRIMITIVO.BOOLEANO) {
      const select = document.createElement('select');
      select.id = idValor;
      select.append(crearOpcion('Verdadero', 'true'), crearOpcion('Falso', 'false'));
      select.value = usarValorPrevio ? String(literalPrevio?.valor === true ? 'true' : 'false') : 'true';
      controlValor = select;
    } else {
      const input = document.createElement('input');
      input.id = idValor;
      input.type = tipo === TIPO_PRIMITIVO.CADENA ? 'text' : 'number';
      if (tipo === TIPO_PRIMITIVO.ENTERO) input.step = '1';
      if (tipo === TIPO_PRIMITIVO.DECIMAL) input.step = 'any';
      if (usarValorPrevio) {
        input.value = String(literalPrevio?.valor ?? '');
      } else if (tipo === TIPO_PRIMITIVO.CADENA) {
        input.value = '';
      } else {
        input.value = '0';
      }
      controlValor = input;
    }
    controlValor.addEventListener('input', recalcularYNotificar);
    controlValor.addEventListener('change', recalcularYNotificar);
    // `controlValor` se reconstruye en cada cambio de tipo (número <->
    // texto <-> select booleano): se recuerda cómo desconectar SUS
    // listeners para que `destruir()` limpie siempre el control vigente,
    // no uno ya reemplazado.
    const elementoActual = controlValor;
    removerListenersValor = () => {
      elementoActual.removeEventListener('input', recalcularYNotificar);
      elementoActual.removeEventListener('change', recalcularYNotificar);
    };
    contenedorValor.append(controlValor);
  }
  /** @type {() => void} */
  let removerListenersValor = () => {};
  renderizarControlValor();
  function manejarCambioTipo() {
    renderizarControlValor();
    recalcularYNotificar();
  }
  selectTipo.addEventListener('change', manejarCambioTipo);
  destructores.push(() => selectTipo.removeEventListener('change', manejarCambioTipo));
  destructores.push(() => removerListenersValor());

  /** @returns {{ valido: true, expresion: NodoModelo } | { valido: false, mensaje: string }} */
  function leerLiteral() {
    const tipo = selectTipo.value;
    // `<input>` y `<select>` comparten `.value`: no hace falta distinguir
    // cuál de los dos es `controlValor` para leer el texto crudo.
    const crudo = String(controlValor?.value ?? '');
    const resultado = validarValorLiteral(tipo, crudo);
    if (!resultado.valido) return resultado;
    return { valido: true, expresion: crearLiteral({ valor: resultado.valor, tipo }) };
  }

  // ---- Sección referencia -------------------------------------------------
  const seccionReferencia = document.createElement('div');
  seccionReferencia.className = 'expr-editor-seccion';

  const idNombre = `${idBase}-referencia-nombre`;
  const etiquetaNombre = document.createElement('label');
  etiquetaNombre.setAttribute('for', idNombre);
  etiquetaNombre.textContent = 'Nombre de la variable';

  const inputNombre = document.createElement('input');
  inputNombre.type = 'text';
  inputNombre.id = idNombre;
  inputNombre.autocomplete = 'off';

  const referenciaPrevia =
    expresionInicial && expresionInicial.nodo === TIPO_NODO.REFERENCIA_VARIABLE ? expresionInicial : null;
  if (referenciaPrevia) {
    inputNombre.value = String(referenciaPrevia.nombre ?? '');
  } else if (variablesDisponibles.length > 0) {
    inputNombre.value = variablesDisponibles[0];
  }

  const idErrorReferencia = `${idBase}-referencia-error`;
  const errorReferencia = document.createElement('p');
  errorReferencia.className = 'expr-editor-error';
  errorReferencia.id = idErrorReferencia;
  errorReferencia.setAttribute('role', 'alert');

  seccionReferencia.append(etiquetaNombre, inputNombre, errorReferencia);

  // Sugerencias de variables ya declaradas (datalist: autocompletado nativo,
  // nunca cierra la entrada a texto libre; ver decisión de la cabecera).
  if (variablesDisponibles.length > 0) {
    const idDatalist = `${idBase}-referencia-lista`;
    const datalist = document.createElement('datalist');
    datalist.id = idDatalist;
    for (const nombreVariable of variablesDisponibles) {
      datalist.append(crearOpcion(nombreVariable, nombreVariable));
    }
    inputNombre.setAttribute('list', idDatalist);
    seccionReferencia.append(datalist);
  }

  inputNombre.addEventListener('input', recalcularYNotificar);
  destructores.push(() => inputNombre.removeEventListener('input', recalcularYNotificar));

  /** @returns {{ valido: true, expresion: NodoModelo } | { valido: false, mensaje: string }} */
  function leerReferencia() {
    const resultado = validarNombreVariable(inputNombre.value);
    if (!resultado.valido) return resultado;
    return { valido: true, expresion: crearReferenciaVariable({ nombre: resultado.valor }) };
  }

  // ---- Sección operación (solo si 'operacion' está permitida) ------------
  // TSK-0069: clase modificadora propia (`expr-editor-seccion--operacion`)
  // para que el trío operando-operador-operando se muestre como una única
  // fila horizontal compacta (ver expression-editor.css), en vez de la
  // disposición genérica de `.expr-editor-seccion` (pensada para
  // etiqueta+control sueltos de las secciones literal/referencia).
  const seccionOperacion = document.createElement('div');
  seccionOperacion.className = 'expr-editor-seccion expr-editor-seccion--operacion';
  /** @type {{ elemento: HTMLFieldSetElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void } | null} */
  let operandoIzquierdo = null;
  /** @type {{ elemento: HTMLFieldSetElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void } | null} */
  let operandoDerecho = null;
  /** @type {HTMLSelectElement | null} */
  let selectOperador = null;
  /** ¿Se ha montado ya la sección "Operación"? Ver `montarSeccionOperacion`. */
  let seccionOperacionMontada = false;

  /**
   * Monta la sección "Operación" (dos operandos + operador), si no lo está ya.
   *
   * PEREZOSA a propósito. Desde que un operando puede ser a su vez otra
   * operación (anidamiento libre, 2026-08-07), construirla de forma ansiosa
   * se recursaría SIN FIN: cada operando montaría su propia sección, cuyos
   * operandos montarían la suya... Antes no pasaba porque los operandos
   * tenían prohibido el modo "Operación" (DEC-024), que es justo la
   * limitación que se levanta.
   *
   * Al construirla solo cuando hace falta -- porque la expresión que llega YA
   * es una operación, o porque el alumno acaba de elegir ese modo -- la
   * recursión la corta el propio dato: un árbol finito monta un árbol finito
   * de controles.
   */
  function montarSeccionOperacion() {
    if (seccionOperacionMontada) return;
    seccionOperacionMontada = true;

    const operacionPrevia =
      expresionInicial && expresionInicial.nodo === TIPO_NODO.OPERACION ? expresionInicial : null;
    const operandosPrevios = operacionPrevia
      ? /** @type {NodoModelo[]} */ (operacionPrevia.operandos)
      : [null, null];

    // Los operandos admiten los MISMOS tres modos que el nivel superior: es
    // lo que permite escribir `nombre + " juega " + nombre`, y lo que hacía
    // falta para poder abrir siquiera un bloque traducido desde Python (el
    // traductor SÍ produce operaciones anidadas, así que el editor lanzaba
    // un TypeError y se llevaba por delante el panel entero).
    operandoIzquierdo = construirSelectorExpresion({
      idBase: `${idBase}-izq`,
      etiquetaLegend: 'Operando izquierdo',
      expresionInicial: operandosPrevios[0] ?? null,
      variablesDisponibles,
      // TSK-0069: orden invertido respecto al operando derecho (Referencia
      // antes que Literal) -- el caso real más común ('edad < 12',
      // 'temperatura >= 40.0') compara una VARIABLE con un valor fijo, así
      // que un operando izquierdo nuevo arranca ya en modo "Variable" sin
      // que el alumno tenga que cambiarlo a mano (ver cabecera del módulo).
      modosPermitidos: [MODO.REFERENCIA, MODO.LITERAL, MODO.OPERACION],
      onCambio: recalcularYNotificar,
      etiquetaLegendModo: 'Tipo de operando',
      esOperando: true,
    });

    const idOperador = `${idBase}-operador`;
    const etiquetaOperador = document.createElement('label');
    etiquetaOperador.setAttribute('for', idOperador);
    etiquetaOperador.textContent = 'Operador';

    selectOperador = document.createElement('select');
    selectOperador.id = idOperador;
    for (const operador of OPERADORES_BINARIOS) {
      selectOperador.append(crearOpcion(ETIQUETA_OPERADOR[operador], operador));
    }
    if (operacionPrevia) {
      selectOperador.value = /** @type {string} */ (operacionPrevia.operador);
    }
    selectOperador.addEventListener('change', recalcularYNotificar);
    destructores.push(() => /** @type {HTMLSelectElement} */ (selectOperador).removeEventListener('change', recalcularYNotificar));

    operandoDerecho = construirSelectorExpresion({
      idBase: `${idBase}-der`,
      etiquetaLegend: 'Operando derecho',
      expresionInicial: operandosPrevios[1] ?? null,
      variablesDisponibles,
      // El operando derecho SÍ mantiene "Literal" primero: es el rol más
      // habitual del segundo operando en las comparaciones sembradas
      // ('... < 12', '... >= 40.0').
      modosPermitidos: [MODO.LITERAL, MODO.REFERENCIA, MODO.OPERACION],
      onCambio: recalcularYNotificar,
      etiquetaLegendModo: 'Tipo de operando',
      esOperando: true,
    });

    seccionOperacion.append(operandoIzquierdo.elemento, etiquetaOperador, selectOperador, operandoDerecho.elemento);
    destructores.push(operandoIzquierdo.destruir, operandoDerecho.destruir);
  }

  // Se monta ya si la expresión que llega ES una operación: sus controles
  // tienen que reflejarla desde el primer instante.
  if (modosPermitidos.includes(MODO.OPERACION) && modo === MODO.OPERACION) {
    montarSeccionOperacion();
  }

  raiz.append(seccionLiteral, seccionReferencia, seccionOperacion);

  function actualizarVisibilidadSeccion() {
    seccionLiteral.hidden = modo !== MODO.LITERAL;
    seccionReferencia.hidden = modo !== MODO.REFERENCIA;
    seccionOperacion.hidden = modo !== MODO.OPERACION;
  }
  actualizarVisibilidadSeccion();

  /** @returns {NodoModelo | null} */
  function obtenerExpresion() {
    if (modo === MODO.LITERAL) {
      const resultado = leerLiteral();
      establecerMensajeError(errorLiteral, resultado.valido ? '' : resultado.mensaje, idErrorLiteral, controlValor ?? selectTipo);
      return resultado.valido ? resultado.expresion : null;
    }
    if (modo === MODO.REFERENCIA) {
      const resultado = leerReferencia();
      establecerMensajeError(errorReferencia, resultado.valido ? '' : resultado.mensaje, idErrorReferencia, inputNombre);
      return resultado.valido ? resultado.expresion : null;
    }
    if (modo === MODO.OPERACION && operandoIzquierdo && operandoDerecho && selectOperador) {
      const izquierda = operandoIzquierdo.obtenerExpresion();
      const derecha = operandoDerecho.obtenerExpresion();
      if (izquierda === null || derecha === null) return null;
      return crearOperacion({ operador: selectOperador.value, operandos: [izquierda, derecha] });
    }
    return null;
  }

  /** Recalcula la expresión resultante y, si es válida, la notifica hacia
   * arriba (al `onCambio` de este nivel: el `onCambio` del nivel superior
   * es a su vez `recalcularYNotificar` del padre cuando este selector es un
   * operando, encadenando la recomposición sin mutar nada). Mientras la
   * expresión no sea válida (p.ej. campo vacío) NO se notifica: se deja el
   * último valor válido notificado sin sobrescribir con `null`. */
  function recalcularYNotificar() {
    const resultado = obtenerExpresion();
    if (resultado !== null) onCambio(resultado);
  }

  for (const radioModo of /** @type {NodeListOf<HTMLInputElement>} */ (grupoModo.querySelectorAll('input[type=radio]'))) {
    const manejarCambioModo = () => {
      if (!radioModo.checked) return;
      modo = radioModo.value;
      // Elegir "Operación" por primera vez es lo que la monta (ver
      // `montarSeccionOperacion`): antes de esto no existe en el DOM.
      if (modo === MODO.OPERACION) montarSeccionOperacion();
      actualizarVisibilidadSeccion();
      recalcularYNotificar();
    };
    radioModo.addEventListener('change', manejarCambioModo);
    destructores.push(() => radioModo.removeEventListener('change', manejarCambioModo));
  }

  function destruir() {
    for (const destructor of destructores) destructor();
  }

  return { elemento: raiz, obtenerExpresion, destruir };
}

/**
 * Valida el parámetro `modoPresentacion` de `crearEditorExpresion` con el
 * mismo patrón que el resto de validadores del módulo (`TypeError` explícito,
 * nunca una interfaz imprevisible en silencio). Acepta los valores de
 * `MODO_PRESENTACION`.
 * @param {unknown} valor
 * @returns {'tecnico' | 'guiado'}
 */
function requerirModoPresentacion(valor) {
  if (valor !== MODO_PRESENTACION.TECNICO && valor !== MODO_PRESENTACION.GUIADO) {
    throw new TypeError(
      `modoPresentacion debe ser '${MODO_PRESENTACION.TECNICO}' o '${MODO_PRESENTACION.GUIADO}'`
    );
  }
  return valor;
}

/**
 * Valida `contextoDidactico`: cadena no vacía o `null`/`undefined` (ausencia).
 * En modo técnico es INERTE (no altera nada); solo la vista guiada lo consume.
 * @param {unknown} valor
 * @returns {string | null}
 */
function requerirContextoDidactico(valor) {
  if (valor === null || valor === undefined) return null;
  if (typeof valor !== 'string') {
    throw new TypeError('contextoDidactico debe ser una cadena o null');
  }
  return valor;
}

// ==========================================================================
// VISTA GUIADA (corte 5+6). Capa didáctica que construye una COMPARACIÓN
// sencilla `[valor] [comparador] [valor]` reutilizando EXACTAMENTE los mismos
// validadores (`validarValorLiteral`/`validarNombreVariable`) y constructores
// del modelo (`crearLiteral`/`crearReferenciaVariable`/`crearOperacion`): no
// duplica lógica de validación ni de serialización. El camino técnico
// (`construirSelectorExpresion`) NO se toca; el modo `'tecnico'` no pasa por
// aquí.
//
// Invariantes de estado:
//   - `expresionActual`: último nodo VÁLIDO; única fuente de `obtenerExpresion`
//     y `onCambio`. Nunca se sustituye por un borrador incompleto o inválido.
//   - `borrador` (los valores vivos de los controles): puede estar incompleto
//     sin corromper `expresionActual`.
//   - `estaHidratando`: protege contra emisiones al montar/hidratar/reconstruir
//     (hidratar o sincronizar la vista NO es una edición del modelo).
// ==========================================================================

/** Los 6 comparadores de la vista guiada (sin aritméticos ni lógicos, que
 * viven en el editor avanzado). */
const COMPARADORES_GUIADOS = Object.freeze([
  OPERADOR.IGUAL,
  OPERADOR.DISTINTO,
  OPERADOR.MAYOR,
  OPERADOR.MENOR,
  OPERADOR.MAYOR_IGUAL,
  OPERADOR.MENOR_IGUAL,
]);

/** Texto natural de cada comparador; se muestra junto al símbolo. */
const ETIQUETA_COMPARADOR_NATURAL = Object.freeze({
  [OPERADOR.IGUAL]: 'es igual a',
  [OPERADOR.DISTINTO]: 'es diferente de',
  [OPERADOR.MAYOR]: 'es mayor que',
  [OPERADOR.MENOR]: 'es menor que',
  [OPERADOR.MAYOR_IGUAL]: 'es mayor o igual que',
  [OPERADOR.MENOR_IGUAL]: 'es menor o igual que',
});

/** Prefijo de la frase didáctica de la vista guiada de CONDICIÓN según el
 * bloque (`contextoDidactico`): la Decisión dice "Si…", el Bucle Mientras
 * "Mientras…", Repetir Hasta "Repetir hasta que…". Por defecto "Si…". */
const PREFIJO_FRASE_CONDICION = Object.freeze({
  condicion: 'Si ',
  'condicion-mientras': 'Mientras ',
  'condicion-hasta': 'Repetir hasta que ',
});

/** Texto natural de TODOS los operadores binarios (para el constructor de VALOR
 * de la Asignación: aritméticos + comparación + lógicos). Se muestra junto al
 * símbolo, igual que en la condición. */
const ETIQUETA_OPERADOR_NATURAL = Object.freeze({
  [OPERADOR.SUMA]: 'más',
  [OPERADOR.RESTA]: 'menos',
  [OPERADOR.MULTIPLICACION]: 'por',
  [OPERADOR.DIVISION]: 'entre',
  [OPERADOR.MODULO]: 'resto de dividir entre',
  [OPERADOR.IGUAL]: 'es igual a',
  [OPERADOR.DISTINTO]: 'es diferente de',
  [OPERADOR.MAYOR]: 'es mayor que',
  [OPERADOR.MENOR]: 'es menor que',
  [OPERADOR.MAYOR_IGUAL]: 'es mayor o igual que',
  [OPERADOR.MENOR_IGUAL]: 'es menor o igual que',
  [OPERADOR.Y]: 'y',
  [OPERADOR.O]: 'o',
});

/** Clases de valor que ofrece cada lado de la comparación guiada. Mapean a los
 * modos/tipos del modelo: variable→ReferenciaVariable; número→Literal
 * entero/decimal (inferido); texto→Literal cadena; booleano→Literal booleano. */
const CLASE = Object.freeze({ VARIABLE: 'variable', NUMERO: 'numero', TEXTO: 'texto', BOOLEANO: 'booleano' });
const CLASES_ORDEN = Object.freeze([CLASE.VARIABLE, CLASE.NUMERO, CLASE.TEXTO, CLASE.BOOLEANO]);
const ETIQUETA_CLASE = Object.freeze({
  [CLASE.VARIABLE]: 'Una variable',
  [CLASE.NUMERO]: 'Un número',
  [CLASE.TEXTO]: 'Un texto',
  [CLASE.BOOLEANO]: 'Verdadero o falso',
});
const AYUDA_CLASE = Object.freeze({
  [CLASE.VARIABLE]: 'Escribe el nombre de un dato, por ejemplo: edad.',
  [CLASE.NUMERO]: 'Escribe un número, por ejemplo: 18 o 3.5.',
  [CLASE.TEXTO]: 'Escribe un texto, por ejemplo: aprobado.',
  [CLASE.BOOLEANO]: 'Elige Verdadero o Falso.',
});

/**
 * Deriva la clase/valor guiado de un nodo operando (Literal o
 * ReferenciaVariable). `null` si el nodo no encaja en ninguna clase guiada.
 * @param {unknown} nodo
 * @returns {{ clase: string, valor: string } | null}
 */
function claseDeNodoOperando(nodo) {
  if (!esNodoDeExpresion(nodo)) return null;
  const n = /** @type {any} */ (nodo);
  if (n.nodo === TIPO_NODO.REFERENCIA_VARIABLE) return { clase: CLASE.VARIABLE, valor: String(n.nombre ?? '') };
  if (n.nodo === TIPO_NODO.LITERAL) {
    if (n.tipo === TIPO_PRIMITIVO.ENTERO || n.tipo === TIPO_PRIMITIVO.DECIMAL) {
      return { clase: CLASE.NUMERO, valor: textoDeNumero(n.valor, n.tipo) };
    }
    if (n.tipo === TIPO_PRIMITIVO.CADENA) return { clase: CLASE.TEXTO, valor: String(n.valor) };
    if (n.tipo === TIPO_PRIMITIVO.BOOLEANO) return { clase: CLASE.BOOLEANO, valor: n.valor === true ? 'true' : 'false' };
  }
  return null;
}

/**
 * Texto del campo numérico para un literal. Un DECIMAL de valor entero (9.0)
 * se escribe "9.0" y no "9": el campo decide entero o decimal por el punto
 * (`evaluarOperandoGuiado`), así que "9" volvía como `9:entero` en cuanto el
 * alumno editaba OTRO valor de la misma expresión, sin haber tocado el 9
 * (architecture-reviewer). La corrección no cambiaba (18 == 18.0), pero la
 * salida de Ejecutar y la traducción sí.
 * @param {unknown} valor
 * @param {string} tipo
 * @returns {string}
 */
function textoDeNumero(valor, tipo) {
  const texto = String(valor);
  return tipo === TIPO_PRIMITIVO.DECIMAL && /^-?\d+$/.test(texto) ? `${texto}.0` : texto;
}

/**
 * Pone `texto` en un aviso o error con role="alert" SOLO si cambia. El lector
 * de pantalla anuncia un alert cada vez que se reescribe su texto, aunque sea
 * el mismo; sin esta guarda repetiría el error en cada pulsación.
 * @param {HTMLElement} elemento
 * @param {string} texto
 */
function ponerTextoSiCambia(elemento, texto) {
  if (elemento.textContent !== texto) elemento.textContent = texto;
}

/**
 * Clasifica `expresionInicial` para la vista guiada de condición:
 *   - `'vacia'`: sin expresión (borrador incompleto de partida).
 *   - `'comparacion'`: comparación representable (operación con comparador y
 *     operandos de un nivel) → se hidratan los controles.
 *   - `'avanzada'`: cualquier otra forma (lógica Y/O, aritmética, variable o
 *     literal suelto, etc.) → fallback al editor técnico, sin simplificar.
 * @param {NodoModelo | null} expresion
 */
function analizarCondicion(expresion) {
  if (expresion === null || expresion === undefined) return { tipo: 'vacia' };
  const e = /** @type {any} */ (expresion);
  if (
    esNodoDeExpresion(expresion) &&
    e.nodo === TIPO_NODO.OPERACION &&
    COMPARADORES_GUIADOS.includes(e.operador) &&
    Array.isArray(e.operandos) &&
    e.operandos.length === 2
  ) {
    const izq = claseDeNodoOperando(e.operandos[0]);
    const der = claseDeNodoOperando(e.operandos[1]);
    if (izq && der) return { tipo: 'comparacion', comparador: e.operador, izq, der };
  }
  return { tipo: 'avanzada' };
}

/** Espacios o comillas dentro de un "nombre de variable": casi siempre es un
 * texto escrito en el sitio equivocado (`juega en`, `" juega en "`). */
const PATRON_PARECE_TEXTO = /[\s"'«»“”‘’]/;
const MENSAJE_VARIABLE_QUE_PARECE_TEXTO =
  'Un nombre de variable no lleva espacios ni comillas. Si querías escribir un texto, elige «Un texto» en «¿Qué quieres usar?».';

/** Clases cuyo control es un campo de texto libre: pasar de una a otra
 * conserva lo escrito (ver `valorQueSobreviveAlCambio`). */
const CLASES_DE_TEXTO_LIBRE = /** @type {readonly string[]} */ (
  Object.freeze([CLASE.VARIABLE, CLASE.TEXTO])
);

/** Pares de comillas que pueden ENVOLVER un texto escrito "a la manera del código". */
const PARES_DE_COMILLAS = Object.freeze([
  ['"', '"'],
  ["'", "'"],
  ['«', '»'],
  ['“', '”'],
]);

/**
 * Quita unas comillas que envuelvan TODO el valor (`" juega en "` →
 * ` juega en `). El contenido de dentro, espacios incluidos, no se toca; si no
 * hay comillas envolventes, el valor vuelve exactamente igual.
 * @param {string} valor
 * @returns {string}
 */
function sinComillasEnvolventes(valor) {
  const recortado = valor.trim();
  for (const [abre, cierra] of PARES_DE_COMILLAS) {
    if (
      recortado.length >= abre.length + cierra.length &&
      recortado.startsWith(abre) &&
      recortado.endsWith(cierra)
    ) {
      return recortado.slice(abre.length, recortado.length - cierra.length);
    }
  }
  return valor;
}

/**
 * ¿Qué conserva el control de valor al cambiar de clase? Entre «Una variable»
 * y «Un texto», lo escrito: es justo el cambio de quien tecleó `juega en` como
 * variable y el aviso le sugiere «Un texto» -- antes el campo se vaciaba y
 * había que volver a escribirlo. Hacia «Un texto» se quitan además unas
 * comillas envolventes (`sinComillasEnvolventes`), porque en un texto guiado
 * las comillas formarían parte del mensaje. En cualquier otro cambio el
 * control empieza vacío, como siempre.
 * @param {string} claseAnterior
 * @param {string} claseNueva
 * @param {string} valor
 * @returns {string | undefined} `undefined` = empezar vacío.
 */
function valorQueSobreviveAlCambio(claseAnterior, claseNueva, valor) {
  if (valor === '') return undefined;
  if (
    !CLASES_DE_TEXTO_LIBRE.includes(claseAnterior) ||
    !CLASES_DE_TEXTO_LIBRE.includes(claseNueva)
  ) {
    return undefined;
  }
  return claseNueva === CLASE.TEXTO ? sinComillasEnvolventes(valor) : valor;
}

/**
 * Evalúa un lado del borrador. Distingue EXPLÍCITAMENTE los tres estados
 * previsibles (sin usar excepciones como flujo): `'incompleto'` (vacío),
 * `'invalido'` (con mensaje del validador existente) o `'valido'` (con el nodo
 * construido por los constructores del modelo).
 * @param {string} clase
 * @param {string} valorCrudo
 * @returns {{ estado: 'incompleto' } | { estado: 'invalido', mensaje: string } | { estado: 'valido', nodo: NodoModelo }}
 */
function evaluarOperandoGuiado(clase, valorCrudo) {
  const crudo = valorCrudo ?? '';
  if (clase === CLASE.BOOLEANO) {
    // El <select> siempre tiene un valor seleccionado: nunca es incompleto.
    return { estado: 'valido', nodo: crearLiteral({ valor: crudo === 'true', tipo: TIPO_PRIMITIVO.BOOLEANO }) };
  }
  if (clase === CLASE.TEXTO) {
    // Un texto es EXACTAMENTE lo escrito, espacios incluidos: " " es el
    // separador que evita que "Pedro" y "Madrid" salgan pegados. Solo un campo
    // vacío de verdad está incompleto. Antes se miraba `crudo.trim()` y un
    // texto hecho solo de espacios se descartaba en silencio: el bloque se
    // quedaba con lo anterior sin avisar.
    if (crudo === '') return { estado: 'incompleto' };
    const r = validarValorLiteral(TIPO_PRIMITIVO.CADENA, crudo);
    return r.valido
      ? { estado: 'valido', nodo: crearLiteral({ valor: r.valor, tipo: TIPO_PRIMITIVO.CADENA }) }
      : { estado: 'invalido', mensaje: r.mensaje };
  }
  if (crudo.trim() === '') return { estado: 'incompleto' };
  if (clase === CLASE.VARIABLE) {
    const r = validarNombreVariable(crudo);
    if (r.valido) return { estado: 'valido', nodo: crearReferenciaVariable({ nombre: r.valor }) };
    return {
      estado: 'invalido',
      mensaje: PATRON_PARECE_TEXTO.test(crudo.trim())
        ? MENSAJE_VARIABLE_QUE_PARECE_TEXTO
        : r.mensaje,
    };
  }
  if (clase === CLASE.NUMERO) {
    const tipo = /[.,]/.test(crudo) ? TIPO_PRIMITIVO.DECIMAL : TIPO_PRIMITIVO.ENTERO;
    const r = validarValorLiteral(tipo, crudo);
    return r.valido
      ? { estado: 'valido', nodo: crearLiteral({ valor: r.valor, tipo }) }
      : { estado: 'invalido', mensaje: r.mensaje };
  }
  // Cualquier otra clase se trata como texto (comportamiento de siempre).
  const r = validarValorLiteral(TIPO_PRIMITIVO.CADENA, crudo);
  return r.valido
    ? { estado: 'valido', nodo: crearLiteral({ valor: r.valor, tipo: TIPO_PRIMITIVO.CADENA }) }
    : { estado: 'invalido', mensaje: r.mensaje };
}

/** @param {string} clase @param {string} valor @returns {string} texto para la frase didáctica. */
function textoNaturalOperando(clase, valor) {
  if (clase === CLASE.TEXTO) return `«${valor}»`;
  if (clase === CLASE.BOOLEANO) return valor === 'true' ? 'Verdadero' : 'Falso';
  return valor;
}

/** @param {string} clase @param {string} valor @returns {string} texto para el código técnico. */
function textoCodigoOperando(clase, valor) {
  if (clase === CLASE.TEXTO) return `"${valor}"`;
  if (clase === CLASE.BOOLEANO) return valor === 'true' ? 'verdadero' : 'falso';
  return valor;
}

/**
 * Un lado (izquierdo/derecho) de la comparación guiada: selector de clase
 * (¿qué quieres usar?) + control de valor acorde a la clase, con su etiqueta,
 * ayuda (`aria-describedby`) y error (asociado al control). Nunca dispara
 * `onInteraccion` por sí mismo durante la hidratación (la programa el llamador
 * al asignar valores de forma programática, que no emiten eventos).
 * @param {{ idLado: string, etiquetaGrupo: string, variablesDisponibles: string[], onInteraccion: () => void }} opciones
 */
function crearLadoGuiado({ idLado, etiquetaGrupo, variablesDisponibles, onInteraccion }) {
  const grupo = document.createElement('div');
  grupo.className = 'expr-guiado-lado';
  grupo.setAttribute('role', 'group');
  grupo.setAttribute('aria-label', etiquetaGrupo);

  const idClase = `${idLado}-clase`;
  const etiquetaClase = document.createElement('label');
  etiquetaClase.setAttribute('for', idClase);
  etiquetaClase.className = 'expr-guiado-etiqueta';
  etiquetaClase.textContent = '¿Qué quieres usar?';

  const selectClase = document.createElement('select');
  selectClase.id = idClase;
  selectClase.className = 'expr-guiado-clase';
  for (const c of CLASES_ORDEN) selectClase.append(crearOpcion(ETIQUETA_CLASE[c], c));

  const contenedorValor = document.createElement('div');
  contenedorValor.className = 'expr-guiado-valor';

  const idAyuda = `${idLado}-ayuda`;
  const ayuda = document.createElement('p');
  ayuda.id = idAyuda;
  ayuda.className = 'expr-guiado-ayuda';

  const idError = `${idLado}-error`;
  const error = document.createElement('p');
  error.id = idError;
  error.className = 'expr-guiado-error';
  // role="alert" (SC 4.1.3): el error aparece mientras el alumno escribe y el
  // aria-describedby solo se lee al volver al campo. Vacío no ocupa sitio
  // (`.expr-guiado-error:empty`) y solo se anuncia cuando el mensaje cambia
  // (`ponerTextoSiCambia`).
  error.setAttribute('role', 'alert');

  grupo.append(etiquetaClase, selectClase, contenedorValor, ayuda, error);

  const idValor = `${idLado}-valor`;
  let clase = /** @type {string} */ (CLASES_ORDEN[0]); // por defecto: variable
  /** @type {HTMLInputElement | HTMLSelectElement | null} */
  let controlValor = null;
  let quitarListenersValor = () => {};

  /** (Re)construye el control de valor acorde a `clase`. Al cambiar de clase el
   * valor se reinicia (estado intermedio VACÍO permitido), salvo entre «Una
   * variable» y «Un texto», que conservan lo escrito (`valorQueSobreviveAlCambio`).
   * @param {string | undefined} valorPrevio */
  function construirControlValor(valorPrevio) {
    quitarListenersValor();
    contenedorValor.replaceChildren();

    const etiquetaValor = document.createElement('label');
    etiquetaValor.setAttribute('for', idValor);
    etiquetaValor.className = 'expr-guiado-etiqueta expr-guiado-etiqueta-valor';

    if (clase === CLASE.BOOLEANO) {
      etiquetaValor.textContent = 'Valor';
      const select = document.createElement('select');
      select.id = idValor;
      select.className = 'expr-guiado-input';
      select.append(crearOpcion('Verdadero', 'true'), crearOpcion('Falso', 'false'));
      contenedorValor.append(etiquetaValor, select);
      controlValor = select;
    } else if (clase === CLASE.NUMERO) {
      etiquetaValor.textContent = 'Número';
      const input = document.createElement('input');
      input.type = 'number';
      input.id = idValor;
      input.className = 'expr-guiado-input';
      input.setAttribute('step', 'any');
      contenedorValor.append(etiquetaValor, input);
      controlValor = input;
    } else if (clase === CLASE.TEXTO) {
      etiquetaValor.textContent = 'Texto';
      const input = document.createElement('input');
      input.type = 'text';
      input.id = idValor;
      input.className = 'expr-guiado-input';
      input.setAttribute('placeholder', 'p. ej. aprobado');
      contenedorValor.append(etiquetaValor, input);
      controlValor = input;
    } else {
      // CLASE.VARIABLE
      etiquetaValor.textContent = 'Nombre de la variable';
      const input = document.createElement('input');
      input.type = 'text';
      input.id = idValor;
      input.className = 'expr-guiado-input';
      if (variablesDisponibles.length > 0) {
        const idLista = `${idLado}-lista`;
        const datalist = document.createElement('datalist');
        datalist.id = idLista;
        for (const v of variablesDisponibles) {
          const opcion = document.createElement('option');
          opcion.value = v;
          datalist.append(opcion);
        }
        input.setAttribute('list', idLista);
        contenedorValor.append(etiquetaValor, input, datalist);
      } else {
        contenedorValor.append(etiquetaValor, input);
      }
      controlValor = input;
    }

    controlValor.setAttribute('aria-describedby', idAyuda);
    if (valorPrevio !== undefined && valorPrevio !== null) controlValor.value = String(valorPrevio);
    ayuda.textContent = AYUDA_CLASE[/** @type {keyof typeof AYUDA_CLASE} */ (clase)];

    const control = controlValor;
    const alEditar = () => onInteraccion();
    control.addEventListener('input', alEditar);
    control.addEventListener('change', alEditar);
    quitarListenersValor = () => {
      control.removeEventListener('input', alEditar);
      control.removeEventListener('change', alEditar);
    };
  }

  const alCambiarClase = () => {
    const claseAnterior = clase;
    const valorAnterior = controlValor ? String(controlValor.value) : '';
    clase = /** @type {string} */ (selectClase.value);
    construirControlValor(valorQueSobreviveAlCambio(claseAnterior, clase, valorAnterior));
    onInteraccion();
  };
  selectClase.addEventListener('change', alCambiarClase);

  construirControlValor(undefined);

  return {
    elemento: grupo,
    obtenerClase: () => clase,
    obtenerValor: () => (controlValor ? String(controlValor.value) : ''),
    /** Hidrata clase+valor de forma programática (no emite eventos).
     * @param {string} nuevaClase @param {string} nuevoValor */
    hidratar: (nuevaClase, nuevoValor) => {
      clase = nuevaClase;
      selectClase.value = nuevaClase;
      construirControlValor(nuevoValor);
    },
    /** Muestra/oculta el error asociado al control (conservando la ayuda).
     * @param {string} mensaje */
    mostrarError: (mensaje) => {
      ponerTextoSiCambia(error, mensaje);
      if (!controlValor) return;
      if (mensaje) {
        controlValor.setAttribute('aria-describedby', `${idAyuda} ${idError}`);
        controlValor.setAttribute('aria-invalid', 'true');
      } else {
        controlValor.setAttribute('aria-describedby', idAyuda);
        controlValor.removeAttribute('aria-invalid');
      }
    },
    /** Deshabilita/rehabilita los controles del lado (cuando la expresión pasa
     * a no ser representable en guiado y solo se edita en avanzado).
     * @param {boolean} deshabilitado */
    deshabilitar: (deshabilitado) => {
      selectClase.disabled = deshabilitado;
      if (controlValor) controlValor.disabled = deshabilitado;
    },
    destruir: () => {
      quitarListenersValor();
      selectClase.removeEventListener('change', alCambiarClase);
    },
  };
}

/**
 * «Opciones avanzadas»: `<details>/<summary>` nativo que monta el editor
 * TÉCNICO embebido, sembrado desde la expresión canónica de la vista guiada.
 * Abrir/cerrar NO modifica la expresión: solo monta o desmonta el editor
 * técnico. Lo comparten las dos vistas guiadas (condición y valor); cada una
 * decide qué hace con una edición técnica (`onCambioTecnico`).
 * @param {{
 *   idBase: string,
 *   variablesDisponibles: string[],
 *   obtenerSemilla: () => NodoModelo | null,
 *   onCambioTecnico: (nuevo: NodoModelo) => void,
 * }} opciones
 * @returns {{ elemento: HTMLDetailsElement, resembrarSiAbierto: () => void, destruir: () => void }}
 */
function crearOpcionesAvanzadas({ idBase, variablesDisponibles, obtenerSemilla, onCambioTecnico }) {
  const detalles = document.createElement('details');
  detalles.className = 'expr-guiado-avanzado';
  const resumen = document.createElement('summary');
  resumen.className = 'expr-guiado-avanzado-summary';
  resumen.textContent = 'Opciones avanzadas';
  const contenido = document.createElement('div');
  contenido.className = 'expr-guiado-avanzado-contenido';
  detalles.append(resumen, contenido);

  /** @type {{ elemento: HTMLElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void } | null} */
  let editorTecnico = null;

  /** (Re)monta el editor TÉCNICO sembrado desde la expresión actual. Su
   * creación NO emite (`construirSelectorExpresion` no notifica en el montaje). */
  function resembrar() {
    if (editorTecnico) {
      editorTecnico.destruir();
      contenido.replaceChildren();
    }
    editorTecnico = construirSelectorExpresion({
      idBase: `${idBase}-avz`,
      etiquetaLegend: 'Expresión',
      expresionInicial: obtenerSemilla(),
      variablesDisponibles,
      modosPermitidos: [MODO.LITERAL, MODO.REFERENCIA, MODO.OPERACION],
      onCambio: onCambioTecnico,
    });
    contenido.append(editorTecnico.elemento);
  }

  const alAlternar = () => {
    if (detalles.open) {
      resembrar();
    } else if (editorTecnico) {
      editorTecnico.destruir();
      editorTecnico = null;
      contenido.replaceChildren();
    }
  };
  detalles.addEventListener('toggle', alAlternar);

  return {
    elemento: detalles,
    resembrarSiAbierto: () => {
      if (detalles.open) resembrar();
    },
    destruir: () => {
      detalles.removeEventListener('toggle', alAlternar);
      if (editorTecnico) editorTecnico.destruir();
    },
  };
}

/**
 * Vista GUIADA de una condición (comparación sencilla) o, si la expresión no
 * es representable, fallback al editor técnico sin perder ni simplificar el
 * nodo. Devuelve el MISMO contrato público que el camino técnico.
 * @param {{ idBase: string, expresionInicial: NodoModelo | null, variablesDisponibles: string[], onCambio: (n: NodoModelo) => void, contextoDidactico: string | null }} opciones
 * @returns {{ elemento: HTMLElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void }}
 */
function construirVistaGuiada({ idBase, expresionInicial, variablesDisponibles, onCambio, contextoDidactico }) {
  const prefijoFrase =
    PREFIJO_FRASE_CONDICION[/** @type {keyof typeof PREFIJO_FRASE_CONDICION} */ (contextoDidactico)] ?? 'Si ';

  const contenedor = document.createElement('div');
  contenedor.className = 'expr-editor expr-editor-guiado';

  const analisis = analizarCondicion(expresionInicial);

  // ---- Fallback avanzado: conserva el nodo intacto y ofrece el editor técnico.
  if (analisis.tipo === 'avanzada') {
    let expresionActual = expresionInicial;
    const aviso = document.createElement('p');
    aviso.className = 'expr-guiado-aviso';
    aviso.textContent =
      'Esta condición usa una configuración avanzada. Puedes editarla sin perder información desde aquí.';

    const tecnico = construirSelectorExpresion({
      idBase: `${idBase}-avz`,
      etiquetaLegend: 'Expresión',
      expresionInicial,
      variablesDisponibles,
      modosPermitidos: [MODO.LITERAL, MODO.REFERENCIA, MODO.OPERACION],
      onCambio: (nueva) => {
        expresionActual = nueva;
        onCambio(nueva);
      },
    });

    contenedor.append(aviso, tecnico.elemento);
    return {
      elemento: contenedor,
      // El nodo original hasta que exista una edición VÁLIDA en el editor técnico.
      obtenerExpresion: () => expresionActual,
      destruir: tecnico.destruir,
    };
  }

  // ---- Vista guiada de comparación (tipo 'vacia' o 'comparacion').
  let expresionActual = analisis.tipo === 'comparacion' ? expresionInicial : null;
  let estaHidratando = false;
  // `estaSincronizando`: durante la re-siembra guiado→técnico o la re-hidratación
  // técnico→guiado, NO se re-emite (evita bucles y emisiones duplicadas).
  // `guiadoDeshabilitado`: la expresión actual (editada en avanzado) no es una
  // comparación representable → los controles guiados quedan deshabilitados y
  // solo se edita en «Opciones avanzadas», SIN simplificar el nodo.
  let estaSincronizando = false;
  let guiadoDeshabilitado = false;

  const fieldset = document.createElement('fieldset');
  fieldset.className = 'expr-guiado';
  const legend = document.createElement('legend');
  legend.className = 'expr-guiado-legend';
  legend.textContent = '¿Qué quieres comprobar?';
  fieldset.append(legend);

  const fila = document.createElement('div');
  fila.className = 'expr-guiado-fila';

  const ladoIzq = crearLadoGuiado({
    idLado: `${idBase}-izq`,
    etiquetaGrupo: 'Primera parte de la condición',
    variablesDisponibles,
    onInteraccion: () => manejarInteraccion(),
  });

  const idComparador = `${idBase}-comparador`;
  const etiquetaComparador = document.createElement('label');
  etiquetaComparador.setAttribute('for', idComparador);
  etiquetaComparador.className = 'expr-guiado-etiqueta';
  etiquetaComparador.textContent = '¿Qué comparación quieres hacer?';
  const selectComparador = document.createElement('select');
  selectComparador.id = idComparador;
  selectComparador.className = 'expr-guiado-comparador';
  for (const op of COMPARADORES_GUIADOS) {
    selectComparador.append(crearOpcion(`${ETIQUETA_COMPARADOR_NATURAL[op]} (${op})`, op));
  }
  const alCambiarComparador = () => manejarInteraccion();
  selectComparador.addEventListener('change', alCambiarComparador);
  const grupoComparador = document.createElement('div');
  grupoComparador.className = 'expr-guiado-comparador-grupo';
  grupoComparador.append(etiquetaComparador, selectComparador);

  const ladoDer = crearLadoGuiado({
    idLado: `${idBase}-der`,
    etiquetaGrupo: 'Segunda parte de la condición',
    variablesDisponibles,
    onInteraccion: () => manejarInteraccion(),
  });

  fila.append(ladoIzq.elemento, grupoComparador, ladoDer.elemento);
  fieldset.append(fila);

  // Vista previa: frase didáctica + código técnico (nunca aria-live sobre cada
  // pulsación; es un refuerzo visual). Muestra un mensaje concreto si falta algo.
  const preview = document.createElement('div');
  preview.className = 'expr-guiado-preview';
  const previewFrase = document.createElement('p');
  previewFrase.className = 'expr-guiado-preview-frase';
  const previewCodigoWrap = document.createElement('p');
  previewCodigoWrap.className = 'expr-guiado-preview-codigo-wrap';
  const previewCodigo = document.createElement('code');
  previewCodigo.className = 'expr-guiado-preview-codigo';
  previewCodigoWrap.append(previewCodigo);
  preview.append(previewFrase, previewCodigoWrap);
  fieldset.append(preview);

  // Aviso cuando la expresión (editada en avanzado) deja de ser una comparación
  // representable: los controles guiados se deshabilitan y solo se edita abajo.
  // role="alert" (SC 4.1.3): aparece por una edición hecha en OTRO sitio de la
  // pantalla, y sin anunciarlo el lector no sabría por qué el guiado dejó de
  // responder. El texto se pone al mostrarlo (`aplicarDeshabilitadoGuiado`).
  const TEXTO_AVISO_NO_REPRESENTABLE =
    'Esta condición usa una configuración avanzada. Edítala aquí abajo, en «Opciones avanzadas»; no se pierde nada.';
  const avisoNoRepresentable = document.createElement('p');
  avisoNoRepresentable.className = 'expr-guiado-aviso';
  avisoNoRepresentable.setAttribute('role', 'alert');
  avisoNoRepresentable.hidden = true;
  fieldset.append(avisoNoRepresentable);

  // «Opciones avanzadas»: el editor TÉCNICO embebido, sembrado desde
  // `expresionActual` (`crearOpcionesAvanzadas`, compartido con la vista de
  // valor). Sincronización EN VIVO bidireccional con los controles guiados
  // (misma fuente `expresionActual`), ver `manejarInteraccion`/`manejarCambioTecnico`.
  const opcionesAvanzadas = crearOpcionesAvanzadas({
    idBase,
    variablesDisponibles,
    obtenerSemilla: () => expresionActual,
    onCambioTecnico: (nuevo) => manejarCambioTecnico(nuevo),
  });
  fieldset.append(opcionesAvanzadas.elemento);

  contenedor.append(fieldset);

  /** @param {ReturnType<typeof evaluarOperandoGuiado>} izq @param {ReturnType<typeof evaluarOperandoGuiado>} der */
  function mensajeFalta(izq, der) {
    if (izq.estado === 'invalido') return 'Revisa el valor de la primera parte de la condición.';
    if (izq.estado === 'incompleto') return 'Elige y completa la primera parte de la condición.';
    if (der.estado === 'invalido') return 'Revisa el valor de la segunda parte de la condición.';
    if (der.estado === 'incompleto') return 'Completa el valor con el que quieres comparar.';
    return 'Completa la condición.';
  }

  /** @param {ReturnType<typeof evaluarOperandoGuiado>} izq @param {ReturnType<typeof evaluarOperandoGuiado>} der */
  function actualizarPreview(izq, der) {
    if (izq.estado === 'valido' && der.estado === 'valido') {
      const comparador = /** @type {keyof typeof ETIQUETA_COMPARADOR_NATURAL} */ (selectComparador.value);
      const ci = ladoIzq.obtenerClase();
      const cd = ladoDer.obtenerClase();
      const vi = ladoIzq.obtenerValor();
      const vd = ladoDer.obtenerValor();
      previewFrase.textContent = `${prefijoFrase}${textoNaturalOperando(ci, vi)} ${ETIQUETA_COMPARADOR_NATURAL[comparador]} ${textoNaturalOperando(cd, vd)}`;
      previewCodigo.textContent = `${textoCodigoOperando(ci, vi)} ${comparador} ${textoCodigoOperando(cd, vd)}`;
      previewCodigoWrap.hidden = false;
    } else {
      previewFrase.textContent = mensajeFalta(izq, der);
      previewCodigo.textContent = '';
      previewCodigoWrap.hidden = true;
    }
  }

  /** Regla de actualización ante una interacción REAL del usuario (guiado). */
  function manejarInteraccion() {
    // No emite durante hidratación/sincronización, ni si el guiado está
    // deshabilitado (la expresión se edita en «Opciones avanzadas»).
    if (estaHidratando || estaSincronizando || guiadoDeshabilitado) return;
    const izq = evaluarOperandoGuiado(ladoIzq.obtenerClase(), ladoIzq.obtenerValor());
    const der = evaluarOperandoGuiado(ladoDer.obtenerClase(), ladoDer.obtenerValor());
    ladoIzq.mostrarError(izq.estado === 'invalido' ? izq.mensaje : '');
    ladoDer.mostrarError(der.estado === 'invalido' ? der.mensaje : '');

    if (izq.estado === 'valido' && der.estado === 'valido') {
      // Borrador VÁLIDO: se promociona a `expresionActual` y se emite UNA vez.
      const operacion = crearOperacion({
        operador: /** @type {string} */ (selectComparador.value),
        operandos: [izq.nodo, der.nodo],
      });
      expresionActual = operacion;
      actualizarPreview(izq, der);
      onCambio(operacion);
      // Sincroniza «Opciones avanzadas» si está abierto (re-siembra, sin emitir).
      opcionesAvanzadas.resembrarSiAbierto();
    } else {
      // Incompleto o inválido: se conserva `expresionActual`, sin emitir.
      actualizarPreview(izq, der);
    }
  }

  /** Edición REAL en «Opciones avanzadas»: promociona a `expresionActual`,
   * emite una vez y sincroniza el guiado (re-hidrata si sigue siendo una
   * comparación representable; si no, lo deshabilita con aviso, SIN simplificar).
   * @param {NodoModelo} nuevo */
  function manejarCambioTecnico(nuevo) {
    if (estaSincronizando) return;
    expresionActual = nuevo;
    sincronizarGuiadoDesde(nuevo);
    onCambio(nuevo);
  }

  /** @param {boolean} deshabilitado */
  function aplicarDeshabilitadoGuiado(deshabilitado) {
    guiadoDeshabilitado = deshabilitado;
    ladoIzq.deshabilitar(deshabilitado);
    ladoDer.deshabilitar(deshabilitado);
    selectComparador.disabled = deshabilitado;
    ponerTextoSiCambia(avisoNoRepresentable, deshabilitado ? TEXTO_AVISO_NO_REPRESENTABLE : '');
    avisoNoRepresentable.hidden = !deshabilitado;
  }

  /** Sincroniza los controles guiados desde un nodo canónico, SIN emitir.
   * @param {NodoModelo | null} nodo */
  function sincronizarGuiadoDesde(nodo) {
    const a = analizarCondicion(nodo);
    estaSincronizando = true;
    if (a.tipo === 'comparacion') {
      const c = /** @type {{ comparador: string, izq: { clase: string, valor: string }, der: { clase: string, valor: string } }} */ (
        a
      );
      ladoIzq.hidratar(c.izq.clase, c.izq.valor);
      ladoDer.hidratar(c.der.clase, c.der.valor);
      selectComparador.value = c.comparador;
      aplicarDeshabilitadoGuiado(false);
      actualizarPreview(
        evaluarOperandoGuiado(ladoIzq.obtenerClase(), ladoIzq.obtenerValor()),
        evaluarOperandoGuiado(ladoDer.obtenerClase(), ladoDer.obtenerValor())
      );
    } else {
      // No representable como comparación guiada: se deshabilita el guiado y se
      // conserva el nodo tal cual (se sigue editando en avanzado).
      aplicarDeshabilitadoGuiado(true);
    }
    estaSincronizando = false;
  }

  // Hidratación inicial (sin emitir): siembra los controles desde la comparación
  // representable, o deja un borrador vacío si no había expresión.
  estaHidratando = true;
  if (analisis.tipo === 'comparacion') {
    const comp = /** @type {{ comparador: string, izq: { clase: string, valor: string }, der: { clase: string, valor: string } }} */ (
      analisis
    );
    ladoIzq.hidratar(comp.izq.clase, comp.izq.valor);
    ladoDer.hidratar(comp.der.clase, comp.der.valor);
    selectComparador.value = comp.comparador;
  }
  estaHidratando = false;
  actualizarPreview(
    evaluarOperandoGuiado(ladoIzq.obtenerClase(), ladoIzq.obtenerValor()),
    evaluarOperandoGuiado(ladoDer.obtenerClase(), ladoDer.obtenerValor())
  );

  return {
    elemento: contenedor,
    obtenerExpresion: () => expresionActual,
    destruir: () => {
      opcionesAvanzadas.destruir();
      selectComparador.removeEventListener('change', alCambiarComparador);
      ladoIzq.destruir();
      ladoDer.destruir();
    },
  };
}

// ==========================================================================
// CADENAS de la vista guiada de VALOR (DEC-115)
//
// «Una operación» en la Asignación es una cadena `v1 op v2 op v3 …`: el alumno
// no ve paréntesis ni árboles, solo una fila de valores y operaciones. Pero la
// IR sí es un árbol binario (`crearOperacion` exige exactamente 2 operandos),
// así que la cadena se convierte en árbol con la precedencia de siempre.
//
// Y al revés, al abrir un bloque: una expresión guardada se muestra como
// cadena SOLO si aplanarla y reconstruirla con esa precedencia devuelve el
// MISMO árbol. Lo que necesita paréntesis (`peso / (altura * altura)`) no lo
// cumple, y sigue abriéndose en el editor técnico, que sí sabe dibujarlo.
// ==========================================================================

/**
 * Precedencia de los operadores binarios de una cadena (mayor = se calcula
 * antes). Todos asocian por la izquierda: `a - b - c` es `(a - b) - c`.
 *
 * Es la MISMA que aplican los traductores: `python-a-ir.js` (or → and → not →
 * comparación → suma/resta → producto/división/módulo, un método por nivel) y
 * `pseudocodigo-a-ir.js`, que usa el analizador de `pseint-interpreter.js`.
 * Allí no hay ninguna tabla que importar -- la precedencia vive en la FORMA de
 * la gramática --, así que se copia aquí y la copia se ata con un test que la
 * compara con LOS DOS traductores para CADA par de operadores:
 * tests/unit/features/translation/lo-traducido-se-puede-editar.spec.js,
 * «la cadena guiada usa la precedencia del traductor».
 */
const PRECEDENCIA_OPERADOR = /** @type {Readonly<Record<string, number>>} */ (
  Object.freeze({
    [OPERADOR.O]: 1,
    [OPERADOR.Y]: 2,
    [OPERADOR.IGUAL]: 3,
    [OPERADOR.DISTINTO]: 3,
    [OPERADOR.MENOR]: 3,
    [OPERADOR.MENOR_IGUAL]: 3,
    [OPERADOR.MAYOR]: 3,
    [OPERADOR.MAYOR_IGUAL]: 3,
    [OPERADOR.SUMA]: 4,
    [OPERADOR.RESTA]: 4,
    [OPERADOR.MULTIPLICACION]: 5,
    [OPERADOR.DIVISION]: 5,
    [OPERADOR.MODULO]: 5,
  })
);

/**
 * @param {string} operador
 * @returns {number}
 */
function precedenciaDe(operador) {
  const precedencia = PRECEDENCIA_OPERADOR[operador];
  if (precedencia === undefined) {
    throw new TypeError(
      `El operador '${operador}' no tiene precedencia en la vista guiada de valor`,
    );
  }
  return precedencia;
}

/**
 * Convierte la cadena `valores[0] operadores[0] valores[1] …` en el árbol de
 * `Operacion` binarias que le corresponde por precedencia (algoritmo de dos
 * pilas, asociatividad izquierda). Ejemplos:
 *   - `nombre + " juega en " + equipo` → `(nombre + " juega en ") + equipo`
 *   - `c * 9 / 5 + 32` → `((c * 9) / 5) + 32`
 *   - `a + b * c` → `a + (b * c)`
 * @param {NodoModelo[]} valores
 * @param {string[]} operadores `valores.length - 1` operadores.
 * @returns {NodoModelo}
 */
function construirArbolConPrecedencia(valores, operadores) {
  if (valores.length !== operadores.length + 1) {
    throw new TypeError('Una cadena necesita exactamente un valor más que operaciones');
  }
  const pilaValores = [valores[0]];
  /** @type {string[]} */
  const pilaOperadores = [];
  const reducir = () => {
    const derecha = /** @type {NodoModelo} */ (pilaValores.pop());
    const izquierda = /** @type {NodoModelo} */ (pilaValores.pop());
    const operador = /** @type {string} */ (pilaOperadores.pop());
    pilaValores.push(crearOperacion({ operador, operandos: [izquierda, derecha] }));
  };
  operadores.forEach((operador, indice) => {
    // `>=` (y no `>`) es lo que hace que operadores de la misma precedencia
    // asocien por la izquierda.
    while (
      pilaOperadores.length > 0 &&
      precedenciaDe(pilaOperadores[pilaOperadores.length - 1]) >= precedenciaDe(operador)
    ) {
      reducir();
    }
    pilaOperadores.push(operador);
    pilaValores.push(valores[indice + 1]);
  });
  while (pilaOperadores.length > 0) reducir();
  return pilaValores[0];
}

/**
 * Recorre en orden un árbol de operaciones binarias y devuelve sus hojas y
 * operadores como una cadena plana. `null` si algún nodo no cabe en una cadena
 * guiada (una llamada, un operador unario, una hoja que no es un valor
 * simple…).
 * @param {unknown} nodo
 * @returns {{ hojas: NodoModelo[], operadores: string[] } | null}
 */
function aplanarCadena(nodo) {
  if (!esNodoDeExpresion(nodo)) return null;
  const n = /** @type {any} */ (nodo);
  if (n.nodo === TIPO_NODO.OPERACION) {
    if (PRECEDENCIA_OPERADOR[n.operador] === undefined) return null;
    if (!Array.isArray(n.operandos) || n.operandos.length !== 2) return null;
    const izquierda = aplanarCadena(n.operandos[0]);
    if (!izquierda) return null;
    const derecha = aplanarCadena(n.operandos[1]);
    if (!derecha) return null;
    return {
      hojas: [...izquierda.hojas, ...derecha.hojas],
      operadores: [...izquierda.operadores, n.operador, ...derecha.operadores],
    };
  }
  return claseDeNodoOperando(nodo) ? { hojas: [nodo], operadores: [] } : null;
}

/**
 * Igualdad estructural de dos expresiones ignorando los `id` (que el editor
 * regenera y no forman parte de la lógica del programa).
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
function mismaExpresionSinIds(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((elemento, indice) => mismaExpresionSinIds(elemento, b[indice]))
    );
  }
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    const ra = /** @type {Record<string, unknown>} */ (a);
    const rb = /** @type {Record<string, unknown>} */ (b);
    const clavesA = Object.keys(ra).filter((clave) => clave !== 'id');
    const clavesB = Object.keys(rb).filter((clave) => clave !== 'id');
    return (
      clavesA.length === clavesB.length &&
      clavesA.every(
        (clave) =>
          Object.prototype.hasOwnProperty.call(rb, clave) &&
          mismaExpresionSinIds(ra[clave], rb[clave]),
      )
    );
  }
  return a === b;
}

/**
 * Clasifica una expresión para la vista guiada de VALOR:
 *   - `'vacio'`: sin expresión (borrador de partida).
 *   - `'simple'`: un valor suelto (variable, número, texto, booleano).
 *   - `'cadena'`: una cadena sin paréntesis (ver cabecera de esta sección).
 *   - `'avanzado'`: cualquier otra cosa → editor técnico, sin simplificar.
 * @param {NodoModelo | null} expresion
 * @returns {{ tipo: 'vacio' }
 *   | { tipo: 'simple', clase: string, valor: string }
 *   | { tipo: 'cadena', cadena: { valores: Array<{ clase: string, valor: string }>, operadores: string[] } }
 *   | { tipo: 'avanzado' }}
 */
function analizarValor(expresion) {
  if (!esNodoDeExpresion(expresion)) return { tipo: 'vacio' };
  const simple = claseDeNodoOperando(expresion);
  if (simple) return { tipo: 'simple', clase: simple.clase, valor: simple.valor };
  const plana = aplanarCadena(expresion);
  if (!plana || plana.operadores.length === 0) return { tipo: 'avanzado' };
  if (
    !mismaExpresionSinIds(construirArbolConPrecedencia(plana.hojas, plana.operadores), expresion)
  ) {
    return { tipo: 'avanzado' };
  }
  const valores = plana.hojas.map(
    (hoja) => /** @type {{ clase: string, valor: string }} */ (claseDeNodoOperando(hoja)),
  );
  return { tipo: 'cadena', cadena: { valores, operadores: plana.operadores } };
}

/** Ordinales para nombrar cada valor de la cadena ("Tercer valor de la operación"). */
const ORDINAL_VALOR = Object.freeze([
  'Primer',
  'Segundo',
  'Tercer',
  'Cuarto',
  'Quinto',
  'Sexto',
  'Séptimo',
  'Octavo',
  'Noveno',
  'Décimo',
]);

/**
 * Nombre accesible del grupo del valor en la posición `indice` (desde 0). Los
 * dos primeros conservan los nombres de siempre.
 * @param {number} indice
 * @returns {string}
 */
function etiquetaGrupoValor(indice) {
  return indice < ORDINAL_VALOR.length
    ? `${ORDINAL_VALOR[indice]} valor de la operación`
    : `Valor ${indice + 1} de la operación`;
}

/**
 * Etiqueta del selector de la operación que precede al valor `indice` (≥ 1).
 * La primera conserva la de siempre; las siguientes dicen a qué valor van, para
 * que ningún selector se quede con un nombre repetido.
 * @param {number} indice
 * @returns {string}
 */
function etiquetaOperadorAntesDe(indice) {
  if (indice <= 1) return '¿Qué operación?';
  return indice < ORDINAL_VALOR.length
    ? `¿Qué operación va antes del ${ORDINAL_VALOR[indice].toLowerCase()} valor?`
    : `¿Qué operación va antes del valor ${indice + 1}?`;
}

/**
 * Un valor de la cadena y, salvo en el primero, la operación que lo precede.
 * @typedef {{
 *   lado: ReturnType<typeof crearLadoGuiado>,
 *   etiquetaOperador: HTMLLabelElement | null,
 *   selectOperador: HTMLSelectElement | null,
 *   botonQuitar: HTMLButtonElement | null,
 *   destruir: () => void,
 * }} EslabonCadena
 */

/**
 * Vista GUIADA de un VALOR (para la Asignación): «¿Qué quieres usar?» con 5
 * opciones (variable / número / texto / verdadero-falso / una operación).
 *
 * «Una operación» es una CADENA: `[valor] [operación ▾] [valor]` y, con el
 * botón «Añadir otro valor», tantos pares `[operación ▾] [valor]` más como
 * hagan falta; cada par añadido tiene su «Quitar este valor». La cadena se
 * convierte en árbol con la precedencia habitual
 * (`construirArbolConPrecedencia`), así que `nombre + " juega en " + equipo`
 * da exactamente la IR anidada de la solución de referencia. Hasta esta
 * ampliación la vista solo sabía una operación de DOS valores (herencia de
 * DEC-024, que DEC-115 ya había superado en el editor técnico): el alumno de
 * "presentacion-jugador" no podía añadir el tercero.
 *
 * Lo que una cadena no puede expresar (paréntesis, llamadas…) se edita en
 * «Opciones avanzadas», el editor técnico embebido, con el MISMO mecanismo que
 * la vista de condición (`crearOpcionesAvanzadas`). Si lo guardado ya tiene
 * esa forma al abrir el bloque, se cede entero al editor técnico, como antes.
 *
 * Reutiliza los mismos validadores y constructores del modelo
 * (`crearLiteral`/`crearReferenciaVariable`/`crearOperacion`).
 * @param {{ idBase: string, expresionInicial: NodoModelo | null, variablesDisponibles: string[], onCambio: (n: NodoModelo) => void }} opciones
 * @returns {{ elemento: HTMLElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void }}
 */
function construirVistaValorGuiada({ idBase, expresionInicial, variablesDisponibles, onCambio }) {
  const CLASE_OPERACION = 'operacion';
  const contenedor = document.createElement('div');
  contenedor.className = 'expr-editor expr-editor-guiado';

  const analisisInicial = analizarValor(expresionInicial);

  // ---- Fallback avanzado: conserva el nodo intacto y ofrece el editor técnico.
  //
  // Con el anidamiento libre (DEC-115) un operando puede ser a su vez otra
  // operación. Antes de existir este fallback, `claseDeNodoOperando` devolvía
  // `null` para ella y se pintaba como un campo de VARIABLE VACÍO: el alumno
  // veía un hueco donde estaba su `a + " juega "`, lo rellenaba, y su trabajo
  // desaparecía sin aviso. Hallazgo de architecture-reviewer (2026-08-07).
  //
  // Hoy las cadenas sin paréntesis SÍ se dibujan en guiado (`analizarValor`);
  // aquí solo llega lo que una cadena no sabe expresar. Mismo remedio que la
  // vista de CONDICIÓN (`analizarCondicion`, tipo 'avanzada').
  if (analisisInicial.tipo === 'avanzado') {
    let expresionAvanzada = expresionInicial;
    const aviso = document.createElement('p');
    aviso.className = 'expr-guiado-aviso';
    aviso.textContent =
      'Este valor usa algo que la vista guiada no sabe dibujar (por ejemplo, paréntesis). Puedes editarlo sin perder nada desde aquí.';

    const tecnico = construirSelectorExpresion({
      idBase: `${idBase}-avz`,
      etiquetaLegend: 'Expresión',
      expresionInicial,
      variablesDisponibles,
      modosPermitidos: [MODO.LITERAL, MODO.REFERENCIA, MODO.OPERACION],
      onCambio: (nueva) => {
        expresionAvanzada = nueva;
        onCambio(nueva);
      },
    });

    contenedor.append(aviso, tecnico.elemento);
    return {
      elemento: contenedor,
      obtenerExpresion: () => expresionAvanzada,
      destruir: tecnico.destruir,
    };
  }

  let expresionActual = esNodoDeExpresion(expresionInicial) ? expresionInicial : null;
  let estaHidratando = false;
  // La expresión (editada en «Opciones avanzadas») dejó de ser dibujable en
  // guiado: los controles guiados quedan deshabilitados, sin simplificar nada.
  let guiadoDeshabilitado = false;

  const fieldset = document.createElement('fieldset');
  fieldset.className = 'expr-guiado';
  const legend = document.createElement('legend');
  legend.className = 'expr-guiado-legend';
  legend.textContent = '¿Qué valor quieres usar?';
  fieldset.append(legend);

  const idClase = `${idBase}-vclase`;
  const etiquetaClase = document.createElement('label');
  etiquetaClase.setAttribute('for', idClase);
  etiquetaClase.className = 'expr-guiado-etiqueta';
  etiquetaClase.textContent = '¿Qué quieres usar?';
  const selectClase = document.createElement('select');
  selectClase.id = idClase;
  selectClase.className = 'expr-guiado-clase';
  for (const c of CLASES_ORDEN) selectClase.append(crearOpcion(ETIQUETA_CLASE[c], c));
  selectClase.append(crearOpcion('Una operación', CLASE_OPERACION));

  const areaValor = document.createElement('div');
  areaValor.className = 'expr-guiado-valor-area';

  const idAyuda = `${idBase}-vayuda`;
  const ayuda = document.createElement('p');
  ayuda.id = idAyuda;
  ayuda.className = 'expr-guiado-ayuda';

  // Error del valor SUELTO (en una cadena, cada valor lleva el suyo). Mismo
  // aspecto y comportamiento que el de cada valor: caja de peligro y
  // role="alert", anunciado solo cuando cambia. Antes iba dentro de la vista
  // previa, en azul informativo y sin anuncio (accessibility-reviewer).
  const idErrorSimple = `${idBase}-verror`;
  const errorSimple = document.createElement('p');
  errorSimple.id = idErrorSimple;
  errorSimple.className = 'expr-guiado-error';
  errorSimple.setAttribute('role', 'alert');

  fieldset.append(etiquetaClase, selectClase, areaValor, ayuda, errorSimple);

  const preview = document.createElement('div');
  preview.className = 'expr-guiado-preview';
  const previewFrase = document.createElement('p');
  previewFrase.className = 'expr-guiado-preview-frase';
  const previewCodigoWrap = document.createElement('p');
  previewCodigoWrap.className = 'expr-guiado-preview-codigo-wrap';
  const previewCodigo = document.createElement('code');
  previewCodigo.className = 'expr-guiado-preview-codigo';
  previewCodigoWrap.append(previewCodigo);
  preview.append(previewFrase, previewCodigoWrap);

  // Mismo aviso que la vista de condición, con role="alert" y el texto puesto
  // al mostrarlo (`aplicarDeshabilitadoGuiado`), por la misma razón.
  const TEXTO_AVISO_NO_REPRESENTABLE =
    'Este valor usa algo que la vista guiada no sabe dibujar (por ejemplo, paréntesis). Edítalo aquí abajo, en «Opciones avanzadas»; no se pierde nada.';
  const avisoNoRepresentable = document.createElement('p');
  avisoNoRepresentable.className = 'expr-guiado-aviso';
  avisoNoRepresentable.setAttribute('role', 'alert');
  avisoNoRepresentable.hidden = true;

  const opcionesAvanzadas = crearOpcionesAvanzadas({
    idBase,
    variablesDisponibles,
    obtenerSemilla: () => expresionActual,
    onCambioTecnico: (nuevo) => manejarCambioTecnico(nuevo),
  });

  fieldset.append(preview, avisoNoRepresentable, opcionesAvanzadas.elemento);
  contenedor.append(fieldset);

  let clase = /** @type {string} */ (CLASE.NUMERO); // por defecto: un número
  /** @type {HTMLInputElement | HTMLSelectElement | null} */
  let controlSimple = null;
  let quitarListenersSimple = () => {};
  /** @type {EslabonCadena[]} */
  let eslabones = [];
  /** @type {HTMLElement | null} */
  let filaCadena = null;
  /** @type {HTMLButtonElement | null} */
  let botonAnadir = null;
  // Nunca se reutiliza: quitar un valor y añadir otro no repite ningún `id`.
  let contadorEslabones = 0;
  const alCambiarOperador = () => alInteractuar();
  const alAnadir = () => anadirOtroValor();

  function limpiarArea() {
    quitarListenersSimple();
    quitarListenersSimple = () => {};
    controlSimple = null;
    for (const eslabon of eslabones) eslabon.destruir();
    eslabones = [];
    if (botonAnadir) {
      botonAnadir.removeEventListener('click', alAnadir);
      botonAnadir = null;
    }
    filaCadena = null;
    areaValor.replaceChildren();
  }

  /** @param {string | undefined} valorPrevio */
  function construirAreaSimple(valorPrevio) {
    const idValor = `${idBase}-vvalor`;
    const etiquetaValor = document.createElement('label');
    etiquetaValor.setAttribute('for', idValor);
    etiquetaValor.className = 'expr-guiado-etiqueta expr-guiado-etiqueta-valor';
    /** @type {HTMLInputElement | HTMLSelectElement} */
    let control;
    if (clase === CLASE.BOOLEANO) {
      etiquetaValor.textContent = 'Valor';
      const sel = document.createElement('select');
      sel.append(crearOpcion('Verdadero', 'true'), crearOpcion('Falso', 'false'));
      control = sel;
    } else if (clase === CLASE.NUMERO) {
      etiquetaValor.textContent = 'Número';
      const inp = document.createElement('input');
      inp.type = 'number';
      inp.setAttribute('step', 'any');
      control = inp;
    } else if (clase === CLASE.TEXTO) {
      etiquetaValor.textContent = 'Texto';
      const inp = document.createElement('input');
      inp.type = 'text';
      inp.setAttribute('placeholder', 'p. ej. aprobado');
      control = inp;
    } else {
      etiquetaValor.textContent = 'Nombre de la variable';
      const inp = document.createElement('input');
      inp.type = 'text';
      if (variablesDisponibles.length > 0) {
        const idLista = `${idBase}-vlista`;
        const datalist = document.createElement('datalist');
        datalist.id = idLista;
        for (const v of variablesDisponibles) {
          const o = document.createElement('option');
          o.value = v;
          datalist.append(o);
        }
        inp.setAttribute('list', idLista);
        areaValor.append(datalist);
      }
      control = inp;
    }
    control.id = idValor;
    control.className = 'expr-guiado-input';
    control.setAttribute('aria-describedby', idAyuda);
    if (valorPrevio !== undefined && valorPrevio !== null) control.value = String(valorPrevio);
    areaValor.append(etiquetaValor, control);
    controlSimple = control;
    ayuda.textContent = AYUDA_CLASE[/** @type {keyof typeof AYUDA_CLASE} */ (clase)];
    const alEditar = () => alInteractuar();
    control.addEventListener('input', alEditar);
    control.addEventListener('change', alEditar);
    quitarListenersSimple = () => {
      control.removeEventListener('input', alEditar);
      control.removeEventListener('change', alEditar);
    };
  }

  /**
   * Añade al final de la cadena un valor (y, salvo el primero, la operación que
   * lo precede). No emite: quien lo llama decide si hay que reevaluar.
   * @param {{ clase: string, valor: string } | null} valorPrevio
   * @param {string | null} operadorPrevio
   * @returns {EslabonCadena}
   */
  function anadirEslabon(valorPrevio, operadorPrevio) {
    const fila = /** @type {HTMLElement} */ (filaCadena);
    const indice = eslabones.length;
    const clave = contadorEslabones;
    contadorEslabones += 1;

    /** @type {HTMLLabelElement | null} */
    let etiquetaOperador = null;
    /** @type {HTMLSelectElement | null} */
    let selectOperador = null;
    /** @type {HTMLElement | null} */
    let grupoOperador = null;
    if (indice > 0) {
      // El primer selector conserva su `id` de siempre.
      const idOp = indice === 1 ? `${idBase}-voperador` : `${idBase}-voperador-${clave}`;
      etiquetaOperador = document.createElement('label');
      etiquetaOperador.setAttribute('for', idOp);
      etiquetaOperador.className = 'expr-guiado-etiqueta';
      selectOperador = document.createElement('select');
      selectOperador.id = idOp;
      selectOperador.className = 'expr-guiado-comparador';
      for (const op of OPERADORES_BINARIOS) {
        selectOperador.append(crearOpcion(`${ETIQUETA_OPERADOR_NATURAL[op]} (${op})`, op));
      }
      if (operadorPrevio) selectOperador.value = operadorPrevio;
      selectOperador.addEventListener('change', alCambiarOperador);
      grupoOperador = document.createElement('div');
      grupoOperador.className = 'expr-guiado-comparador-grupo';
      grupoOperador.append(etiquetaOperador, selectOperador);
      fila.append(grupoOperador);
    }

    // Los dos primeros conservan sus `id` de siempre; los añadidos, un sufijo
    // con el contador.
    const sufijo = indice === 0 ? 'vizq' : indice === 1 ? 'vder' : `vextra${clave}`;
    const lado = crearLadoGuiado({
      idLado: `${idBase}-${sufijo}`,
      etiquetaGrupo: etiquetaGrupoValor(indice),
      variablesDisponibles,
      onInteraccion: () => alInteractuar(),
    });
    if (valorPrevio) lado.hidratar(valorPrevio.clase, valorPrevio.valor);
    fila.append(lado.elemento);

    // Solo los valores AÑADIDOS se pueden quitar: una operación siempre tiene
    // al menos dos. El botón vive dentro del grupo del valor, así que un
    // lector de pantalla lo anuncia con su contexto ("Tercer valor…").
    /** @type {HTMLButtonElement | null} */
    let botonQuitar = null;
    const alQuitar = () => quitarEslabon(eslabon);
    if (indice >= 2) {
      botonQuitar = document.createElement('button');
      botonQuitar.type = 'button';
      botonQuitar.className = 'expr-guiado-boton expr-guiado-boton--quitar';
      botonQuitar.textContent = 'Quitar este valor';
      botonQuitar.addEventListener('click', alQuitar);
      lado.elemento.append(botonQuitar);
    }

    const selectFinal = selectOperador;
    const botonFinal = botonQuitar;
    const grupoFinal = grupoOperador;
    /** @type {EslabonCadena} */
    const eslabon = {
      lado,
      etiquetaOperador,
      selectOperador,
      botonQuitar,
      destruir: () => {
        lado.destruir();
        if (selectFinal) selectFinal.removeEventListener('change', alCambiarOperador);
        if (botonFinal) botonFinal.removeEventListener('click', alQuitar);
        if (grupoFinal) grupoFinal.remove();
        lado.elemento.remove();
      },
    };
    eslabones.push(eslabon);
    return eslabon;
  }

  /** Vuelve a nombrar grupos y selectores tras añadir o quitar un valor. */
  function renumerar() {
    eslabones.forEach((eslabon, indice) => {
      eslabon.lado.elemento.setAttribute('aria-label', etiquetaGrupoValor(indice));
      if (eslabon.etiquetaOperador)
        eslabon.etiquetaOperador.textContent = etiquetaOperadorAntesDe(indice);
    });
  }

  function anadirOtroValor() {
    if (guiadoDeshabilitado || !filaCadena) return;
    // La operación nueva copia la anterior: lo habitual es seguir sumando
    // (o multiplicando) igual que antes.
    const anterior = eslabones[eslabones.length - 1];
    const operadorAnterior =
      anterior && anterior.selectOperador ? anterior.selectOperador.value : null;
    const nuevo = anadirEslabon(null, operadorAnterior);
    renumerar();
    // Queda un valor vacío: la vista previa lo dice, y NO se emite nada.
    alInteractuar();
    // Foco al primer control nuevo, en orden de lectura.
    if (nuevo.selectOperador) nuevo.selectOperador.focus();
  }

  /** @param {EslabonCadena} eslabon */
  function quitarEslabon(eslabon) {
    const indice = eslabones.indexOf(eslabon);
    if (indice < 2 || guiadoDeshabilitado) return;
    eslabon.destruir();
    eslabones = eslabones.filter((otro) => otro !== eslabon);
    renumerar();
    alInteractuar();
    // El foco nunca cae a `document.body` (03-frontend §13.1): «Añadir otro
    // valor» nunca se destruye mientras haya cadena, mismo criterio que
    // "Quitar argumento" en block-content-panel.js.
    if (botonAnadir) botonAnadir.focus();
  }

  /** @param {{ valores: Array<{ clase: string, valor: string } | null>, operadores: Array<string | null> } | null} cadena */
  function construirAreaOperacion(cadena) {
    filaCadena = document.createElement('div');
    filaCadena.className = 'expr-guiado-fila';
    botonAnadir = document.createElement('button');
    botonAnadir.type = 'button';
    botonAnadir.className = 'expr-guiado-boton';
    botonAnadir.textContent = 'Añadir otro valor';
    botonAnadir.addEventListener('click', alAnadir);
    areaValor.append(filaCadena, botonAnadir);
    ayuda.textContent =
      'Combina valores con operaciones, por ejemplo: nota + 1. Con «Añadir otro valor» puedes seguir: nombre + " juega en " + equipo.';
    const cantidad = Math.max(2, cadena ? cadena.valores.length : 0);
    for (let indice = 0; indice < cantidad; indice += 1) {
      const valorPrevio = cadena ? (cadena.valores[indice] ?? null) : null;
      const operadorPrevio = cadena && indice > 0 ? (cadena.operadores[indice - 1] ?? null) : null;
      anadirEslabon(valorPrevio, operadorPrevio);
    }
    renumerar();
  }

  /**
   * @param {string | undefined} valorPrevioSimple
   * @param {{ valores: Array<{ clase: string, valor: string } | null>, operadores: Array<string | null> } | null} cadena
   */
  function renderArea(valorPrevioSimple, cadena) {
    limpiarArea();
    if (clase === CLASE_OPERACION) construirAreaOperacion(cadena);
    else construirAreaSimple(valorPrevioSimple);
  }

  /** @returns {ReturnType<typeof evaluarOperandoGuiado>} */
  function evaluarValor() {
    if (clase === CLASE_OPERACION) {
      if (eslabones.length < 2) return { estado: 'incompleto' };
      const evaluados = eslabones.map((eslabon) =>
        evaluarOperandoGuiado(eslabon.lado.obtenerClase(), eslabon.lado.obtenerValor()),
      );
      eslabones.forEach((eslabon, indice) => {
        const evaluado = evaluados[indice];
        eslabon.lado.mostrarError(evaluado.estado === 'invalido' ? evaluado.mensaje : '');
      });
      /** @type {NodoModelo[]} */
      const nodos = [];
      for (const evaluado of evaluados) if (evaluado.estado === 'valido') nodos.push(evaluado.nodo);
      if (nodos.length === evaluados.length) {
        const operadores = eslabones
          .slice(1)
          .map((eslabon) => /** @type {HTMLSelectElement} */ (eslabon.selectOperador).value);
        return { estado: 'valido', nodo: construirArbolConPrecedencia(nodos, operadores) };
      }
      return evaluados.some((evaluado) => evaluado.estado === 'invalido')
        ? { estado: 'invalido', mensaje: 'Revisa los valores de la operación.' }
        : { estado: 'incompleto' };
    }
    return evaluarOperandoGuiado(clase, controlSimple ? String(controlSimple.value) : '');
  }

  function textoDelValor() {
    if (clase === CLASE_OPERACION && eslabones.length >= 2) {
      /** @type {string[]} */
      const natural = [];
      /** @type {string[]} */
      const codigo = [];
      eslabones.forEach((eslabon, indice) => {
        if (indice > 0 && eslabon.selectOperador) {
          const op = /** @type {keyof typeof ETIQUETA_OPERADOR_NATURAL} */ (
            eslabon.selectOperador.value
          );
          natural.push(ETIQUETA_OPERADOR_NATURAL[op]);
          codigo.push(op);
        }
        const c = eslabon.lado.obtenerClase();
        const v = eslabon.lado.obtenerValor();
        natural.push(textoNaturalOperando(c, v));
        codigo.push(textoCodigoOperando(c, v));
      });
      return { nat: natural.join(' '), cod: codigo.join(' ') };
    }
    const v = controlSimple ? String(controlSimple.value) : '';
    return { nat: textoNaturalOperando(clase, v), cod: textoCodigoOperando(clase, v) };
  }

  /** @param {boolean} valido */
  function actualizarPreview(valido) {
    if (valido) {
      const { nat, cod } = textoDelValor();
      previewFrase.textContent = `El valor será ${nat}`;
      previewCodigo.textContent = cod;
      previewCodigoWrap.hidden = false;
    } else {
      previewFrase.textContent = 'Completa el valor.';
      previewCodigo.textContent = '';
      previewCodigoWrap.hidden = true;
    }
  }

  /**
   * Muestra (o retira) el error del valor SUELTO y marca su control. En una
   * cadena queda vacío: cada valor muestra el suyo (`crearLadoGuiado#mostrarError`).
   * @param {string} mensaje Vacío = válido o incompleto.
   */
  function marcarErrorSimple(mensaje) {
    ponerTextoSiCambia(errorSimple, mensaje);
    if (!controlSimple) return;
    if (mensaje) {
      controlSimple.setAttribute('aria-invalid', 'true');
      controlSimple.setAttribute('aria-describedby', `${idAyuda} ${idErrorSimple}`);
    } else {
      controlSimple.removeAttribute('aria-invalid');
      controlSimple.setAttribute('aria-describedby', idAyuda);
    }
  }

  function alInteractuar() {
    if (estaHidratando || guiadoDeshabilitado) return;
    const r = evaluarValor();
    marcarErrorSimple(clase !== CLASE_OPERACION && r.estado === 'invalido' ? r.mensaje : '');
    if (r.estado === 'valido') {
      expresionActual = r.nodo;
      actualizarPreview(true);
      onCambio(r.nodo);
      // «Opciones avanzadas» abierto: se re-siembra desde el nodo nuevo, sin emitir.
      opcionesAvanzadas.resembrarSiAbierto();
    } else {
      actualizarPreview(false);
    }
  }

  /**
   * Pinta los controles guiados desde un análisis ya hecho. No emite: quien la
   * llama pone `estaHidratando`.
   * @param {ReturnType<typeof analizarValor>} analisis
   */
  function hidratarDesde(analisis) {
    if (analisis.tipo === 'cadena') {
      clase = CLASE_OPERACION;
      selectClase.value = CLASE_OPERACION;
      renderArea(undefined, analisis.cadena);
    } else if (analisis.tipo === 'simple') {
      clase = analisis.clase;
      selectClase.value = analisis.clase;
      renderArea(analisis.valor, null);
    } else {
      clase = CLASE.NUMERO;
      selectClase.value = CLASE.NUMERO;
      renderArea(undefined, null);
    }
  }

  /** @param {boolean} deshabilitado */
  function aplicarDeshabilitadoGuiado(deshabilitado) {
    guiadoDeshabilitado = deshabilitado;
    selectClase.disabled = deshabilitado;
    if (controlSimple) controlSimple.disabled = deshabilitado;
    for (const eslabon of eslabones) {
      eslabon.lado.deshabilitar(deshabilitado);
      if (eslabon.selectOperador) eslabon.selectOperador.disabled = deshabilitado;
      if (eslabon.botonQuitar) eslabon.botonQuitar.disabled = deshabilitado;
    }
    if (botonAnadir) botonAnadir.disabled = deshabilitado;
    ponerTextoSiCambia(avisoNoRepresentable, deshabilitado ? TEXTO_AVISO_NO_REPRESENTABLE : '');
    avisoNoRepresentable.hidden = !deshabilitado;
  }

  /**
   * Edición REAL en «Opciones avanzadas»: promociona a `expresionActual`, emite
   * una vez y sincroniza el guiado (re-hidrata si se puede dibujar; si no, lo
   * deshabilita con aviso, SIN simplificar el nodo).
   * @param {NodoModelo} nuevo
   */
  function manejarCambioTecnico(nuevo) {
    expresionActual = nuevo;
    const analisis = analizarValor(nuevo);
    if (analisis.tipo === 'avanzado') {
      aplicarDeshabilitadoGuiado(true);
    } else {
      estaHidratando = true;
      hidratarDesde(analisis);
      estaHidratando = false;
      aplicarDeshabilitadoGuiado(false);
      // Lo que llega del técnico es un nodo válido: ningún error pendiente.
      marcarErrorSimple('');
      actualizarPreview(evaluarValor().estado === 'valido');
    }
    onCambio(nuevo);
  }

  const alCambiarClase = () => {
    const claseAnterior = clase;
    const valorAnterior = controlSimple ? String(controlSimple.value) : '';
    clase = /** @type {string} */ (selectClase.value);
    if (clase === CLASE_OPERACION) {
      // Lo ya escrito pasa a ser el PRIMER valor de la operación: quien tenía
      // `nombre` y quiere añadirle algo no tiene que volver a teclearlo.
      const semilla =
        claseAnterior !== CLASE_OPERACION && valorAnterior !== ''
          ? { valores: [{ clase: claseAnterior, valor: valorAnterior }], operadores: [] }
          : null;
      renderArea(undefined, semilla);
    } else {
      const conservado =
        claseAnterior === CLASE_OPERACION
          ? undefined
          : valorQueSobreviveAlCambio(claseAnterior, clase, valorAnterior);
      renderArea(conservado, null);
    }
    alInteractuar();
  };
  selectClase.addEventListener('change', alCambiarClase);

  // Hidratación inicial (sin emitir).
  estaHidratando = true;
  hidratarDesde(analisisInicial);
  estaHidratando = false;
  actualizarPreview(evaluarValor().estado === 'valido');

  return {
    elemento: contenedor,
    obtenerExpresion: () => expresionActual,
    destruir: () => {
      selectClase.removeEventListener('change', alCambiarClase);
      opcionesAvanzadas.destruir();
      limpiarArea();
    },
  };
}

/**
 * Widget público: editor estructurado de una expresión: literal, referencia a
 * variable u operación binaria, con anidamiento libre (DEC-115; hasta
 * entonces DEC-024 lo limitaba a un nivel). Ver cabecera del módulo para el
 * razonamiento de diseño.
 *
 * Ampliación aditiva y retrocompatible: `modoPresentacion` y
 * `contextoDidactico` son OPCIONALES. Omitirlos —o pasar `modoPresentacion:
 * 'tecnico'`— produce exactamente el mismo DOM y comportamiento que el editor
 * técnico de siempre (no pasa por la infraestructura guiada); `contextoDidactico`
 * es inerte en modo técnico. `modoPresentacion: 'guiado'` construye la vista
 * guiada de comparación (`construirVistaGuiada`) o, con `contextoDidactico:
 * 'valor'`, la de valor con cadenas (`construirVistaValorGuiada`); las dos
 * ceden al editor técnico ante lo que no saben dibujar. El retorno es idéntico
 * en todos los casos.
 * @param {{
 *   expresionInicial?: NodoModelo | null,
 *   variablesDisponibles?: string[],
 *   onCambio: (nuevaExpresion: NodoModelo) => void,
 *   modoPresentacion?: 'tecnico' | 'guiado',
 *   contextoDidactico?: string | null,
 * }} opciones
 * @returns {{ elemento: HTMLElement, obtenerExpresion: () => NodoModelo | null, destruir: () => void }}
 */
export function crearEditorExpresion({
  expresionInicial = null,
  variablesDisponibles = [],
  onCambio,
  modoPresentacion = MODO_PRESENTACION.TECNICO,
  contextoDidactico = null,
}) {
  requerirFuncion(onCambio, 'onCambio');
  requerirArrayDeCadenas(variablesDisponibles, 'variablesDisponibles');
  requerirModoPresentacion(modoPresentacion);
  requerirContextoDidactico(contextoDidactico);

  contadorInstancias += 1;
  const idBase = `expr-editor-${contadorInstancias}`;

  // Modo GUIADO: `contextoDidactico` elige la vista.
  //   - 'valor' → constructor de VALOR (Asignación): 5 clases; «Una operación»
  //     es una cadena de valores con la precedencia habitual.
  //   - resto ('condicion'/'condicion-mientras'/'condicion-hasta') → vista de
  //     comparación (Decisión / bucles), con su prefijo de frase.
  if (modoPresentacion === MODO_PRESENTACION.GUIADO) {
    if (contextoDidactico === 'valor') {
      return construirVistaValorGuiada({ idBase, expresionInicial, variablesDisponibles, onCambio });
    }
    return construirVistaGuiada({ idBase, expresionInicial, variablesDisponibles, onCambio, contextoDidactico });
  }

  // Camino TÉCNICO (sin cambios respecto al editor de siempre): no pasa por la
  // infraestructura guiada, preservando su estabilidad y sus tests.
  const contenedor = document.createElement('div');
  contenedor.className = 'expr-editor';

  const selector = construirSelectorExpresion({
    idBase,
    etiquetaLegend: 'Expresión',
    expresionInicial,
    variablesDisponibles,
    modosPermitidos: [MODO.LITERAL, MODO.REFERENCIA, MODO.OPERACION],
    onCambio,
  });

  contenedor.append(selector.elemento);

  return {
    elemento: contenedor,
    obtenerExpresion: selector.obtenerExpresion,
    destruir: selector.destruir,
  };
}
